import { createBuilder, defineQueries, defineQuery } from "@rocicorp/zero"
import { z } from "zod"

import { parseThreadNumber } from "@productlane/lib/src/thread-number"

import type { Membership, Schema, User } from "./schema"
import type { Condition, ExpressionBuilder, Query, Row } from "@rocicorp/zero"
import { schema } from "./schema"

const builder = createBuilder(schema)

export const notificationFilterSchema = z.object({
	view: z.enum(["inbox", "unread", "mentions", "archived"]),
	reasons: z.array(z.string()).min(1).max(20).optional(),
	types: z.array(z.string()).min(1).max(60).optional(),
	actorIds: z.array(z.string()).min(1).max(50).optional(),
	/** Rows with no person behind them: agent, SLA, billing, imports. */
	systemActor: z.boolean().optional(),
})

export type NotificationFilter = z.infer<typeof notificationFilterSchema>

/** Mirrored in Prisma by notificationFilterWhere, which "select all" uses to reach rows past the synced page. */
function whereNotificationFilter(
	query: Query<"notifications", Schema>,
	filter: NotificationFilter,
) {
	let q = query
	if (filter.view === "archived") {
		q = q.where("archivedAt", "IS NOT", null)
	} else {
		q = q.where("archivedAt", "IS", null)
		if (filter.view === "unread") q = q.where("readAt", "IS", null)
		if (filter.view === "mentions") q = q.where("reason", "mentioned")
	}
	if (filter.reasons) q = q.where("reason", "IN", filter.reasons)
	if (filter.types) q = q.where("type", "IN", filter.types)
	// Filters stay on Notification's own columns: a thread exists() (company,
	// state) reads every inbox row, and flipped it scans the workspace's threads.
	const { actorIds, systemActor } = filter
	if (actorIds || systemActor) {
		q = q.where(({ or, cmp }) =>
			or(
				...(actorIds ? [cmp("actorId", "IN", actorIds)] : []),
				...(systemActor ? [cmp("actorId", "IS", null)] : []),
			),
		)
	}
	return q
}

const inboxStateSchema = z.enum([
	"needs-response",
	"new",
	"open",
	"my",
	"my-snoozed",
	"my-done",
	"my-close-loop",
	"replied",
	"snoozed",
	"done",
	"close-loop",
	"sla-breached",
	"custom-view",
	"agent-resolved",
	"agent-escalated",
	"agent-reviewed",
	"agent-pending",
	"agent-loops-closed",
])

const stateScopeSchema = z.enum(["open", "snoozed", "done", "close-loop"])

const sortByColumnSchema = z.enum([
	"lastMessageAt",
	"lastStateChangeAt",
	"painLevel",
	"snoozedUntil",
])

const companiesSortColumnSchema = z.enum([
	"name",
	"createdAt",
	"revenue",
	"size",
	"tierName",
	"statusName",
])

const companiesCursorSchema = z.object({
	name: z.string(),
	createdAt: z.number(),
	revenue: z.number().nullable(),
	size: z.number().nullable(),
	tierName: z.string().nullable(),
	statusName: z.string().nullable(),
	id: z.string(),
})

const issueOrderBySchema = z.enum([
	"createdAt",
	"updatedAt",
	"sortOrder",
	"title",
	"status",
	"priority",
	"totalScore",
])

const painLevelSchema = z.enum(["HIGH", "MEDIUM", "LOW", "UNKNOWN"])

const feedbackOriginSchema = z.enum([
	"API",
	"INAPP",
	"PORTAL",
	"SUPPORT_PORTAL",
	"EMAIL",
	"SLACK",
	"SLACK_CONNECT",
	"INTERCOM",
	"INTERCOM_ATTACHMENT",
	"ZENDESK",
	"ZENDESK_ATTACHMENT",
	"FRONT_ATTACHMENT",
	"ZAPIER",
	"HUBSPOT",
	"PLAIN",
	"CALENDAR",
	"WIDGET",
	"AI_CHAT",
	"UPVOTE",
	"LIVE_CHAT",
	"TEAMS",
	"LINEAR",
	"CUSTOM",
])

const sortByColumnOrder = z.enum(["asc", "desc"])
export const roadmapSortOrderSchema = z.enum([
	"CUSTOM",
	"SCORE_DESC",
	"SCORE_ASC",
	"LINEAR",
	"LINEAR_REVERSE",
])
export type RoadmapSortOrder = z.infer<typeof roadmapSortOrderSchema>

const roadmapTabSchema = z.enum(["active", "hidden", "pending", "all"])

type InboxState = z.infer<typeof inboxStateSchema>
type SortByColumn = z.infer<typeof sortByColumnSchema>
type SortByColumnOrder = z.infer<typeof sortByColumnOrder>
type RoadmapTab = z.infer<typeof roadmapTabSchema>
type PainLevel = z.infer<typeof painLevelSchema>
type FeedbackOrigin = z.infer<typeof feedbackOriginSchema>

const feedbackStateSchema = z.enum([
	"NEW",
	"PROCESSED",
	"COMPLETED",
	"SNOOZED",
	"UNSNOOZED",
])

export type FeedbackStateFilter = z.infer<typeof feedbackStateSchema>

const replyStateSchema = z.enum(["NEEDS_RESPONSE", "REPLIED"])

export type ReplyStateFilter = z.infer<typeof replyStateSchema>

const threadNegationsSchema = z.object({
	origins: z.boolean().optional(),
	emailAddressIds: z.boolean().optional(),
	portalInstanceIds: z.boolean().optional(),
	creatorIds: z.boolean().optional(),
	painLevels: z.boolean().optional(),
	assigneeIds: z.boolean().optional(),
	projectIds: z.boolean().optional(),
	tagIds: z.boolean().optional(),
	issueIds: z.boolean().optional(),
	companyIds: z.boolean().optional(),
	personIds: z.boolean().optional(),
	statuses: z.boolean().optional(),
	replyStates: z.boolean().optional(),
	companyTiers: z.boolean().optional(),
	companyStatuses: z.boolean().optional(),
	companyOwnerIds: z.boolean().optional(),
	slackWorkspaceIds: z.boolean().optional(),
	slackChannelIds: z.boolean().optional(),
})

export type ThreadFilterNegations = z.infer<typeof threadNegationsSchema>

export const threadFiltersSchema = z.object({
	negations: threadNegationsSchema.optional(),
	origins: z.array(feedbackOriginSchema.nullable()).optional(),
	emailAddressIds: z.array(z.string().nullable()).optional(),
	portalInstanceIds: z.array(z.string().nullable()).optional(),
	creatorIds: z.array(z.string()).optional(),
	painLevels: z.array(painLevelSchema).optional(),
	assigneeIds: z.array(z.string().nullable()).optional(),
	projectIds: z.array(z.string().nullable()).optional(),
	tagIds: z.array(z.string().nullable()).optional(),
	issueIds: z.array(z.string().nullable()).optional(),
	companyIds: z.array(z.string().nullable()).optional(),
	personIds: z.array(z.string().nullable()).optional(),
	statuses: z.array(feedbackStateSchema).optional(),
	replyStates: z.array(replyStateSchema).optional(),
	companyTiers: z.array(z.string()).optional(),
	companyStatuses: z.array(z.string()).optional(),
	companyOwnerIds: z.array(z.string().nullable()).optional(),
	issueStatuses: z.array(z.string()).optional(),
	/** SlackConnectSettings ids: the Slack workspaces threads came through. */
	slackWorkspaceIds: z.array(z.string()).max(50).optional(),
	/** Slack's own channel ids, as stored on Feedback.slackChannelId. */
	slackChannelIds: z.array(z.string()).max(500).optional(),
	companyRevenue: z
		.object({
			operator: z.enum([">=", "<=", "=", "!="]),
			value: z.number(),
		})
		.nullable()
		.optional(),
	companySize: z
		.object({
			operator: z.enum([">=", "<=", "=", "!="]),
			value: z.number(),
		})
		.nullable()
		.optional(),
})

export type ThreadFilters = z.infer<typeof threadFiltersSchema>

export const SOURCE_VALUES = [
	"attio",
	"hubspot",
	"intercom",
	"productboard",
	"zendesk",
	"productlane",
] as const
export type SourceValue = (typeof SOURCE_VALUES)[number]

const sourcesSchema = z.array(z.enum(SOURCE_VALUES)).optional()

const companiesNegationsSchema = z.object({
	tiers: z.boolean().optional(),
	statuses: z.boolean().optional(),
	ownerIds: z.boolean().optional(),
	sources: z.boolean().optional(),
})

export type CompaniesFilterNegations = z.infer<typeof companiesNegationsSchema>

const companiesFiltersSchema = z.object({
	negations: companiesNegationsSchema.optional(),
	tiers: z.array(z.string()).optional(),
	statuses: z.array(z.string()).optional(),
	ownerIds: z.array(z.string().nullable()).optional(),
	sources: sourcesSchema,
	revenue: z
		.object({
			operator: z.enum([">=", "<=", "=", "!="]),
			value: z.number(),
		})
		.nullable()
		.optional(),
	size: z
		.object({
			operator: z.enum([">=", "<=", "=", "!="]),
			value: z.number(),
		})
		.nullable()
		.optional(),
})

type CompaniesFilters = z.infer<typeof companiesFiltersSchema>

export const companiesSelectionSchema = z.object({
	filters: companiesFiltersSchema.optional(),
	search: z.string().max(200).optional(),
})

export type CompaniesSelection = z.infer<typeof companiesSelectionSchema>

const contactsNegationsSchema = z.object({
	companyIds: z.boolean().optional(),
	subscribedStates: z.boolean().optional(),
	sources: z.boolean().optional(),
})

export type ContactsFilterNegations = z.infer<typeof contactsNegationsSchema>

const contactsFiltersSchema = z.object({
	negations: contactsNegationsSchema.optional(),
	companyIds: z.array(z.string().nullable()).optional(),
	subscribedStates: z.array(z.boolean()).optional(),
	sources: sourcesSchema,
})

type ContactsFilters = z.infer<typeof contactsFiltersSchema>

const roadmapNegationsSchema = z.object({
	statuses: z.boolean().optional(),
	teamIds: z.boolean().optional(),
	projectIds: z.boolean().optional(),
	priorities: z.boolean().optional(),
	companyIds: z.boolean().optional(),
	labelIds: z.boolean().optional(),
})

export type RoadmapFilterNegations = z.infer<typeof roadmapNegationsSchema>

const roadmapFiltersSchema = z.object({
	negations: roadmapNegationsSchema.optional(),
	statuses: z.array(z.string()).optional(),
	teamIds: z.array(z.string()).optional(),
	projectIds: z.array(z.string().nullable()).optional(),
	priorities: z.array(z.string()).optional(),
	companyIds: z.array(z.string().nullable()).optional(),
	sources: z.array(z.enum(["customers", "internal"])).optional(),
	labelIds: z.array(z.string()).optional(),
})

export type RoadmapFilters = z.infer<typeof roadmapFiltersSchema>
export type RoadmapSource = "customers" | "internal"

/** The one source a filter asks for; both or neither means no source filter. */
export function roadmapSource(
	sources: readonly RoadmapSource[] | undefined,
): RoadmapSource | null {
	return sources?.length === 1 ? sources[0]! : null
}

/** Whether the filter excludes labels, which the client has to check itself. */
export function excludesRoadmapLabels(
	filters: Pick<RoadmapFilters, "labelIds" | "negations"> | undefined,
): boolean {
	return !!filters?.negations?.labelIds && (filters.labelIds?.length ?? 0) > 0
}

/**
 * "Label is not" needs not(exists()), which the Zero client cannot run, so it
 * checks the labels the rows load. A project counts if any of its issues has one.
 */
interface LabeledRow {
	readonly labels?: ReadonlyArray<{ labelId: string }>
}

// Takes any row: the roadmap queries only load labels while a label is excluded.
export function matchesRoadmapLabels(
	row: object,
	filters: Pick<RoadmapFilters, "labelIds" | "negations"> | undefined,
): boolean {
	if (!excludesRoadmapLabels(filters)) return true
	const excluded = new Set(filters?.labelIds)
	const { labels, issues } = row as LabeledRow & {
		readonly issues?: readonly LabeledRow[]
	}
	const own = labels ?? issues?.flatMap((issue) => issue.labels ?? [])
	return !(own ?? []).some((label) => excluded.has(label.labelId))
}

/**
 * "Internal" needs not(exists()) on customerNeeds, which the Zero client cannot
 * run, so it is applied to the loaded rows, which carry customerNeeds already.
 */
export function matchesRoadmapSource(
	row: { customerNeeds?: readonly unknown[] },
	sources: readonly RoadmapSource[] | undefined,
): boolean {
	const source = roadmapSource(sources)
	if (source === null) return true
	const fromCustomers = (row.customerNeeds?.length ?? 0) > 0
	return source === "customers" ? fromCustomers : !fromCustomers
}

// Newest events per message type synced by threadWithFullDetails before the
// user asks for older history.
export const feedbackDetailMessagePageSize = 30
// Activities are tiny rows and churn fastest; a bigger page keeps them from
// pushing the gapless cut boundary above real customer messages.
export const feedbackDetailActivityPageSize = 300
// DoS guard for the base window, not a UX ceiling: older history loads via
// threadOlderMessages cursor pages, the base only grows to heal slide-out.
export const feedbackDetailMessageLimitMax = 10_000
export const feedbackOlderMessagesPageSize = 100

export interface ThreadTimelineCursor {
	createdAt: number
	id: string
}

// Display limits per relation. Queries fetch one extra sentinel row past each
// limit so the client can tell "exactly limit rows" from "more exist".
export function threadDetailRelationLimits(limit?: number) {
	const messageLimit = Math.min(
		limit ?? feedbackDetailMessagePageSize,
		feedbackDetailMessageLimitMax,
	)
	return {
		messageLimit,
		activityLimit: Math.max(messageLimit, feedbackDetailActivityPageSize),
	}
}

// Zero keys a query on its name plus JSON args, so `{ threadId }` and
// `{ threadId, limit: 30 }` sync twice. Every threadWithFullDetails caller uses this.
export function threadDetailQueryArgs(
	threadId: string,
	limit: number = feedbackDetailMessagePageSize,
) {
	return { threadId, limit }
}

const PICKER_LIST_LIMIT = 100

const pickerListArgs = z
	.object({ query: z.string().optional(), limit: z.number().optional() })
	.optional()

function pickerSearch(args: z.infer<typeof pickerListArgs>): string | null {
	const query = stripLikeWildcards(args?.query?.trim().toLowerCase() ?? "")
	return query ? `%${query}%` : null
}

export const contactsSortColumnSchema = z.enum(["name", "createdAt", "status"])

export const contactsSelectionSchema = z.object({
	filters: contactsFiltersSchema.optional(),
	search: z.string().optional(),
})
export type ContactsSelection = z.infer<typeof contactsSelectionSchema>

export const NOTIFICATION_INBOX_MAX_LIMIT = 200

export const queries = defineQueries({
	currentWorkspace: defineQuery(({ ctx: authData }) => {
		return builder.workspaces
			.where("id", authData.workspaceId)
			.one()
			.related("emailSettings")
			.related("slackConnectSettings")
			.related("roadmapSettings")
			.related("customerPortalSettings")
			.related("intercomSettings")
			.related("hubspotSettings")
			.related("plainSettings")
			.related("teamsSettings")
			.related("githubSettings")
			.related("widgetSettings")
			.related("aiChatSettings")
			.related("widgetLinks")
			.related("sidebarSections", (s) =>
				s.orderBy("order", "asc").related("sidebarSectionHeaders"),
			)
			.related("snippets")
			.related("slaPolicies")
			.related("ssoSettings")
			.related("helpCenterHomeTranslations")
			.related("aiChatSettingsTranslations")
			.related("portalInstances")
	}),

	// Kept out of `currentWorkspace`: that query already sits at TypeScript's
	// instantiation ceiling.
	portalInstanceSso: defineQuery(({ ctx: authData }) => {
		return builder.portalInstances
			.where("workspaceId", authData.workspaceId)
			.related("ssoSettings", (s) => s.one())
	}),

	userWorkspaces: defineQuery(({ ctx: authData }) => {
		return builder.workspaces.whereExists("memberships", (m) =>
			m.where("userId", authData.userId),
		)
	}),

	currentUser: defineQuery(({ ctx: authData }) => {
		return builder.users
			.where("id", authData.userId)
			.related("memberships", (m) =>
				m
					.where("workspaceId", authData.workspaceId)
					.one()
					.related("personalSlackNotificationSettings"),
			)
			.one()
	}),

	users: defineQuery(({ ctx: authData }) => {
		return builder.users
			.whereExists("memberships", (m) =>
				m.where("workspaceId", authData.workspaceId),
			)
			.orderBy("name", "asc")
	}),

	projects: defineQuery(({ ctx: authData }) => {
		return authorizedProjects(authData.workspaceId).where("isDeleted", false)
	}),

	tags: defineQuery(({ ctx: authData }) => {
		return authorizedTags(authData.workspaceId).where("isDeleted", false)
	}),

	issues: defineQuery(
		z
			.object({
				orderBy: issueOrderBySchema,
				order: sortByColumnOrder.optional(),
			})
			.optional(),
		({ ctx: authData, args }) => {
			let q = authorizedIssues(authData.workspaceId).where("isDeleted", false)
			if (args?.orderBy) {
				q = q.orderBy(args.orderBy, args.order ?? "asc")
			}
			return q
		},
	),

	contacts: defineQuery(
		z.object({ filters: contactsFiltersSchema.optional() }),
		({ ctx: authData, args }) => {
			let q = authorizedContacts(authData.workspaceId).where("isDeleted", false)
			if (args.filters) {
				q = applyContactsFilters(q, args.filters)
			}
			return q
		},
	),

	// One page of the People list, with the companies its company column names.
	contactsPage: defineQuery(
		contactsSelectionSchema.extend({
			sortColumn: contactsSortColumnSchema.optional(),
			sortOrder: sortByColumnOrder.optional(),
			limit: z.number().max(5000),
		}),
		({ ctx: authData, args }) => {
			const order = args.sortOrder ?? "asc"
			const reverse = order === "asc" ? "desc" : "asc"
			let q = contactsSelectionQuery(authData.workspaceId, args)
				.related("company")
				.related("companyLinks", (link) => link.related("company"))
			switch (args.sortColumn ?? "createdAt") {
				case "name":
					q = q.orderBy("name", order).orderBy("email", order)
					break
				case "status":
					// Subscribed, then unsubscribed, then blocked.
					q = q
						.orderBy("isEmailSuppressed", order)
						.orderBy("isSubscribed", reverse)
						.orderBy("name", "asc")
					break
				default:
					q = q.orderBy("createdAt", order)
			}
			return q.orderBy("id", order).limit(args.limit)
		},
	),

	contactCompanyLinks: defineQuery(({ ctx: authData }) => {
		return builder.contactCompanies.where("workspaceId", authData.workspaceId)
	}),

	contactByEmail: defineQuery(z.string(), ({ ctx: authData, args: email }) => {
		return authorizedContacts(authData.workspaceId)
			.where("isDeleted", false)
			.where("email", "=", email)
			.one()
	}),

	// The senders one thread's emails name, instead of every contact in the workspace.
	contactsByEmails: defineQuery(
		z.array(z.string()).max(200),
		({ ctx: authData, args: emails }) => {
			return authorizedContacts(authData.workspaceId)
				.where("isDeleted", false)
				.where("email", "IN", emails)
		},
	),

	contactById: defineQuery(z.string(), ({ ctx: authData, args: contactId }) => {
		return authorizedContacts(authData.workspaceId).where("id", contactId).one()
	}),

	companies: defineQuery(
		z.object({ filters: companiesFiltersSchema.optional() }),
		({ ctx: authData, args }) => {
			let q = authorizedCompanies(authData.workspaceId).where(
				"isDeleted",
				false,
			)
			if (args.filters) {
				q = applyCompaniesFilters(q, args.filters)
			}
			return q
		},
	),

	// Picker and filter lists: searched on the server and capped, never the whole table.
	companyOptions: defineQuery(pickerListArgs, ({ ctx: authData, args }) => {
		const search = pickerSearch(args)
		const companies = authorizedCompanies(authData.workspaceId).where(
			"isDeleted",
			false,
		)
		return (
			search
				? companies.where(({ or, cmp }) =>
						or(cmp("name", "ILIKE", search), cmp("domain", "ILIKE", search)),
					)
				: companies
		)
			.orderBy("name", "asc")
			.orderBy("id", "asc")
			.limit(args?.limit ?? PICKER_LIST_LIMIT)
	}),

	contactOptions: defineQuery(pickerListArgs, ({ ctx: authData, args }) => {
		const search = pickerSearch(args)
		const contacts = authorizedContacts(authData.workspaceId).where(
			"isDeleted",
			false,
		)
		return (
			search
				? contacts.where(({ or, cmp }) =>
						or(cmp("name", "ILIKE", search), cmp("email", "ILIKE", search)),
					)
				: contacts
		)
			.orderBy("name", "asc")
			.orderBy("email", "asc")
			.limit(args?.limit ?? PICKER_LIST_LIMIT)
	}),

	// The rows a filter already selected, which a capped list may not include.
	companiesByIds: defineQuery(
		z.array(z.string()).max(PICKER_LIST_LIMIT),
		({ ctx: authData, args: ids }) =>
			authorizedCompanies(authData.workspaceId)
				.where("id", "IN", ids)
				.where("isDeleted", false),
	),

	contactsByIds: defineQuery(
		z.array(z.string()).max(PICKER_LIST_LIMIT),
		({ ctx: authData, args: ids }) =>
			authorizedContacts(authData.workspaceId)
				.where("id", "IN", ids)
				.where("isDeleted", false),
	),

	// A few of one company's people, for the contact picker's suggestions.
	contactsByCompany: defineQuery(
		z.object({ companyId: z.string(), limit: z.number().max(50) }),
		({ ctx: authData, args }) =>
			authorizedContacts(authData.workspaceId)
				.where("companyId", args.companyId)
				.where("isDeleted", false)
				.limit(args.limit),
	),

	// Capped and searched on the server: the uncapped list synced a full thread per row.
	companiesWithRequests: defineQuery(
		pickerListArgs,
		({ ctx: authData, args }) => {
			const search = pickerSearch(args)
			const companies = authorizedCompanies(authData.workspaceId).where(
				"isDeleted",
				false,
			)
			return (
				search
					? companies.where(({ or, cmp }) =>
							or(cmp("name", "ILIKE", search), cmp("domain", "ILIKE", search)),
						)
					: companies
			)
				.whereExists("feedbacks", (f) =>
					f
						.where("workspaceId", authData.workspaceId)
						.where("isDeleted", false),
				)
				.orderBy("name", "asc")
				.limit(args?.limit ?? PICKER_LIST_LIMIT)
		},
	),

	contactsWithRequests: defineQuery(
		pickerListArgs,
		({ ctx: authData, args }) => {
			const search = pickerSearch(args)
			const contacts = authorizedContacts(authData.workspaceId).where(
				"isDeleted",
				false,
			)
			return (
				search
					? contacts.where(({ or, cmp }) =>
							or(cmp("name", "ILIKE", search), cmp("email", "ILIKE", search)),
						)
					: contacts
			)
				.whereExists("feedbacks", (f) =>
					f
						.where("workspaceId", authData.workspaceId)
						.where("isDeleted", false),
				)
				.orderBy("name", "asc")
				.orderBy("email", "asc")
				.limit(args?.limit ?? PICKER_LIST_LIMIT)
		},
	),

	issueById: defineQuery(z.string(), ({ ctx: authData, args: issueId }) => {
		return authorizedIssues(authData.workspaceId)
			.where("id", issueId)
			.where("isDeleted", false)
			.one()
	}),

	companyById: defineQuery(z.string(), ({ ctx: authData, args: companyId }) => {
		return authorizedCompanies(authData.workspaceId)
			.where("id", companyId)
			.where("isDeleted", false)
			.one()
	}),

	// The selected row a picker renders, logo included, so the picker can leave
	// the workspace-wide company list unsynced until someone opens it.
	companyWithMetaById: defineQuery(
		z.string(),
		({ ctx: authData, args: companyId }) => {
			return authorizedCompanies(authData.workspaceId)
				.where("id", companyId)
				.where("isDeleted", false)
				.related("companyMeta")
				.one()
		},
	),

	threadCount: defineQuery(
		z.object({
			tab: inboxStateSchema.optional(),
			filters: threadFiltersSchema.optional(),
			stateScope: stateScopeSchema.optional(),
			nowMs: z.number().optional(),
			limit: z.number().int().min(1).max(100).optional(),
		}),
		({ ctx: authData, args }) => {
			const q = orderThreadCount(
				inboxThreads(authData.workspaceId),
				args.tab,
			).limit(args.limit ?? 100)
			return filterInboxThreads(q, authData, args)
		},
	),

	threadSearch: defineQuery(
		z.object({
			tab: inboxStateSchema.optional(),
			filters: threadFiltersSchema.optional(),
			stateScope: stateScopeSchema.optional(),
			limit: z.number().optional(),
			sortColumn: sortByColumnSchema.optional(),
			sortOrder: sortByColumnOrder.optional(),
			nowMs: z.number().optional(),
			startAfter: z
				.object({
					createdAt: z.number(),
					lastStateChangeAt: z.number().nullable(),
					lastMessageAt: z.number(),
					painLevel: painLevelSchema,
					snoozedUntil: z.number().nullable(),
					id: z.string(),
				})
				.optional(),
		}),
		({ ctx: authData, args }) => {
			const { limit, sortColumn, sortOrder, startAfter } = args
			let q = applySortFilters(
				inboxThreads(authData.workspaceId),
				sortColumn ?? "lastMessageAt",
				sortOrder ?? "desc",
			)
			q = filterInboxThreads(q, authData, args)

			if (startAfter !== undefined) {
				q = q.start(startAfter)
			}

			if (limit !== undefined) {
				q = q.limit(limit)
			}

			return applyThreadListRelations(q, authData.workspaceId)
		},
	),

	threadByID: defineQuery(z.string(), ({ ctx: authData, args: id }) => {
		const q = authorizedThreads(authData.workspaceId).where("id", id)
		return applyThreadListRelations(q, authData.workspaceId).one()
	}),

	// Small per workspace (a handful of rows), so the docs nav filters by portal on the client like groups.
	helpCenterTabs: defineQuery(({ ctx: authData }) => {
		return builder.helpCenterTabs
			.where("workspaceId", authData.workspaceId)
			.orderBy("order", "asc")
	}),

	// At most a few per portal, so the dashboard filters by portal on the client.
	helpCenterCustomHomeSections: defineQuery(({ ctx: authData }) => {
		return builder.helpCenterCustomHomeSections
			.where("workspaceId", authData.workspaceId)
			.orderBy("order", "asc")
	}),

	helpCenterArticlesWithGroups: defineQuery(({ ctx: authData }) => {
		return authorizedHelpCenterGroups(authData.workspaceId)
			.related("helpCenterArticles", (q) =>
				q
					.where("isDeleted", false)
					.where("parentArticleId", "IS", null)
					.orderBy("order", "asc")
					.related("translations")
					// Two hops of subpages: the tree only renders one, and the second
					// hop is what surfaces a row that a bad drag left too deep.
					.related("subpages", (sq) =>
						sq
							.where("isDeleted", false)
							.orderBy("order", "asc")
							.related("translations")
							.related("subpages", (gq) =>
								gq
									.where("isDeleted", false)
									.orderBy("order", "asc")
									.related("translations"),
							),
					),
			)
			.related("translations")
			.orderBy("order", "asc")
	}),

	// The group-rooted tree query skips an article that belongs to no group, so
	// the docs review view resolves its target by id instead.
	helpCenterArticleById: defineQuery(
		z.string(),
		({ ctx: authData, args: id }) => {
			return authorizedHelpCenterArticles(authData.workspaceId)
				.where("id", id)
				.where("isDeleted", false)
				.related("translations")
				.one()
		},
	),

	helpCenterArticleSubpages: defineQuery(
		z.string(),
		({ ctx: authData, args: parentId }) => {
			return builder.helpCenterArticles
				.where("workspaceId", "=", authData.workspaceId)
				.where("parentArticleId", "=", parentId)
				.where("isDeleted", "=", false)
				.orderBy("order", "asc")
				.related("translations")
		},
	),

	helpCenterArticleVersions: defineQuery(
		z.object({ articleId: z.string(), language: z.string() }),
		({ args: { articleId, language } }) => {
			return builder.helpCenterArticleVersions
				.where("articleId", "=", articleId)
				.where("language", "=", language)
				.related("createdBy")
				.orderBy("createdAt", "desc")
		},
	),

	openProposalsForWorkspace: defineQuery(({ ctx: authData }) => {
		// Only standalone proposals — grouped proposals surface via
		// openProposalGroupsForWorkspace so the inbox renders one row per group.
		// Ownerless DRAFTs come along too: the v2 API writes those when a draft
		// is created with submit_for_review false, and with no author they are
		// reachable from no other screen.
		return builder.helpCenterArticleProposals
			.where("workspaceId", "=", authData.workspaceId)
			.where(({ or, and, cmp }) =>
				or(
					cmp("status", "=", "OPEN"),
					and(cmp("status", "=", "DRAFT"), cmp("createdByUserId", "IS", null)),
				),
			)
			.where("proposalGroupId", "IS", null)
			.orderBy("createdAt", "desc")
			.related("article")
			.related("baseVersion")
			.related("proposedGroup", (g) =>
				g.where("workspaceId", authData.workspaceId).related("tab"),
			)
	}),

	closedProposalsForArticle: defineQuery(
		z.string(),
		({ ctx: authData, args: articleId }) =>
			builder.helpCenterArticleProposals
				.where("workspaceId", "=", authData.workspaceId)
				.where("articleId", "=", articleId)
				.where(({ or, cmp }) =>
					or(
						cmp("status", "=", "ACCEPTED"),
						cmp("status", "=", "REJECTED"),
						cmp("status", "=", "SUPERSEDED"),
					),
				)
				.orderBy("closedAt", "desc")
				.related("baseVersion")
				.related("createdBy")
				.related("closedBy")
				.related("group"),
	),

	proposalTranslations: defineQuery(
		z.string(),
		({ ctx: authData, args: proposalId }) =>
			builder.helpCenterArticleProposalTranslations
				.where("workspaceId", "=", authData.workspaceId)
				.where("proposalId", "=", proposalId),
	),

	openProposalGroupsForWorkspace: defineQuery(({ ctx: authData }) => {
		return builder.helpCenterProposalGroups
			.where("workspaceId", "=", authData.workspaceId)
			.where("status", "=", "OPEN")
			.orderBy("createdAt", "desc")
			.related("proposals", (q) =>
				q
					.related("article")
					.related("baseVersion")
					.related("proposedGroup", (g) =>
						g.where("workspaceId", authData.workspaceId).related("tab"),
					),
			)
			.related("issues")
			.related("sourceThreads")
	}),

	// The Docs badge only counts rows by target article, so it skips the article and version bodies.
	openProposalGroupsForCount: defineQuery(({ ctx: authData }) => {
		return builder.helpCenterProposalGroups
			.where("workspaceId", "=", authData.workspaceId)
			.where("status", "=", "OPEN")
			.orderBy("createdAt", "desc")
			.related("proposals")
	}),

	proposalGroupById: defineQuery(
		z.string(),
		({ ctx: authData, args: groupId }) => {
			return builder.helpCenterProposalGroups
				.where("workspaceId", "=", authData.workspaceId)
				.where("id", "=", groupId)
				.related("proposals", (q) =>
					q
						.related("article")
						.related("baseVersion")
						.related("proposedGroup", (g) =>
							g.where("workspaceId", authData.workspaceId).related("tab"),
						),
				)
				.related("issues")
				.related("sourceThreads")
				.one()
		},
	),

	// Every open card the review wrote; the limit only guards a runaway review.
	openAgentRecommendations: defineQuery(({ ctx: authData }) => {
		return builder.agentRecommendations
			.where("workspaceId", "=", authData.workspaceId)
			.where("status", "=", "OPEN")
			.orderBy("impactPerMonth", "desc")
			.orderBy("createdAt", "desc")
			.limit(500)
	}),

	// One idea by id, in any status, so an open card sees a teammate's decision live.
	agentRecommendation: defineQuery(
		z.object({ id: z.string() }),
		({ ctx: authData, args }) => {
			return builder.agentRecommendations
				.where("workspaceId", "=", authData.workspaceId)
				.where("id", "=", args.id)
				.one()
		},
	),

	// The Agent page's Accepted and Declined tabs: the latest decisions first.
	closedAgentRecommendations: defineQuery(
		z.object({ status: z.enum(["ACCEPTED", "DISMISSED"]) }),
		({ ctx: authData, args }) => {
			return builder.agentRecommendations
				.where("workspaceId", "=", authData.workspaceId)
				.where("status", "=", args.status)
				.orderBy("updatedAt", "desc")
				.limit(50)
		},
	),

	// Decided docs suggestions for the same tabs. A declined group can still read
	// ACCEPTED, so the page sorts them by their proposals' own status.
	closedProposalGroups: defineQuery(({ ctx: authData }) => {
		return builder.helpCenterProposalGroups
			.where("workspaceId", "=", authData.workspaceId)
			.where("status", "IN", ["ACCEPTED", "REJECTED"])
			.orderBy("closedAt", "desc")
			.limit(100)
			.related("proposals")
	}),

	// The idea a teammate decided most recently, so the Assistant home shows it handled.
	lastClosedAgentRecommendation: defineQuery(({ ctx: authData }) => {
		return builder.agentRecommendations
			.where("workspaceId", "=", authData.workspaceId)
			.where("status", "IN", ["ACCEPTED", "DISMISSED"])
			.where("closedByUserId", "IS NOT", null)
			.orderBy("updatedAt", "desc")
			.one()
	}),

	// The user's own assistant chats for the Agent sidebar and its View all page.
	// Leads on the (workspaceId, userId, lastMessageAt) index; a chat without a
	// message has no lastMessageAt and stays out.
	myAssistantChats: defineQuery(
		z.object({ archived: z.boolean(), limit: z.number().int().max(1000) }),
		({ ctx: authData, args }) => {
			return builder.aiChatSessions
				.where("workspaceId", "=", authData.workspaceId)
				.where("userId", "=", authData.userId)
				.where("isDeleted", false)
				.where("lastMessageAt", "IS NOT", null)
				.where("archivedAt", args.archived ? "IS NOT" : "IS", null)
				.orderBy("lastMessageAt", "desc")
				.limit(args.limit)
		},
	),

	// The thread's customer-facing agent session, for its resolution state in
	// the thread sidebar. Leads on the (workspaceId, threadId, userId) index.
	threadAgentSession: defineQuery(
		z.object({ threadId: z.string() }),
		({ ctx: authData, args }) =>
			agentSessions(builder.aiChatSessions, authData.workspaceId)
				.where("threadId", "=", args.threadId)
				.orderBy("createdAt", "asc")
				.one(),
	),

	// The rows the Agent page reads a session's outcome from, so the thread badge
	// classifies it the same way. A handful per thread at most.
	threadAgentOutcomeActivities: defineQuery(
		z.object({ threadId: z.string() }),
		({ ctx: authData, args }) =>
			builder.threadActivities
				.where("workspaceId", authData.workspaceId)
				.where("threadId", args.threadId)
				.where("isDeleted", false)
				.where("eventType", "IN", ["ai_handoff", "ai_resolution_changed"]),
	),

	// One of the user's own chats by id, for its archive state on the chat page.
	myAssistantChat: defineQuery(
		z.object({ id: z.string() }),
		({ ctx: authData, args }) =>
			builder.aiChatSessions
				.where("workspaceId", "=", authData.workspaceId)
				.where("userId", "=", authData.userId)
				.where("id", "=", args.id)
				.one(),
	),

	// Bug Inspect caps a workspace at five runs a day, so this stays small.
	agentCodeFixes: defineQuery(({ ctx: authData }) => {
		return builder.codingAgentRuns
			.where("workspaceId", "=", authData.workspaceId)
			.where("provider", "=", "bug-inspect")
			.where(({ cmp, or }) =>
				or(cmp("reviewState", "=", "PENDING"), cmp("status", "=", "RUNNING")),
			)
			.related("issue", (q) => q.one())
			.related("feedback", (q) => q.one())
			.orderBy("createdAt", "desc")
			.limit(50)
	}),

	// The Agent page's Chats: conversations where a person still has to step in.
	// Payload checks (skip ground, rating) and "customer waiting" run on the
	// client, since ZQL cannot read inside json() or compare two columns.
	agentChatsNeedingPerson: defineQuery(
		z.object({ sinceMs: z.number() }),
		({ ctx: authData, args }) => {
			const ws = authData.workspaceId
			return authorizedThreads(ws)
				.where("isDeleted", false)
				.where("origin", "!=", "UPVOTE")
				.where("replied", false)
				.where("agentEscalationReviewedAt", "IS", null)
				.where("lastMessageAt", ">=", args.sinceMs)
				.where(({ and, cmp }) => and(cmp("state", "IN", OPEN_STATES)))
				.where(({ or, and, cmp, exists }) =>
					or(
						// A few hundred signals a month drive the join; the createdAt
						// bound keeps it from reading every one ever written.
						exists(
							"threadActivities",
							(a) =>
								agentChatSignals(a, ws).where("createdAt", ">=", args.sinceMs),
							{ flip: true },
						),
						// Agent replies on these channels bump lastOutboundMessageAt, so
						// the client can tell the customer wrote last. Not flipped: the
						// thread filters above leave a few hundred rows, while a workspace
						// holds thousands of unresolved sessions.
						and(
							cmp("origin", "NOT IN", ["AI_CHAT", "LIVE_CHAT"]),
							cmp("lastInboundMessageAt", "IS NOT", null),
							exists("aiChatSessions", (s) =>
								agentSessions(s, ws)
									.where(({ or, cmp }) =>
										or(cmp("isResolved", false), cmp("isResolved", "IS", null)),
									)
									.where("escalatedAt", "IS", null),
							),
						),
					),
				)
				.related("contact", (c) => c.one())
				.related("threadActivities", (a) =>
					agentChatSignals(a, ws).orderBy("createdAt", "desc").limit(5),
				)
				.orderBy("lastMessageAt", "desc")
				.orderBy("id", "desc")
				.limit(200)
		},
	),

	myDraftProposals: defineQuery(({ ctx: authData }) => {
		return builder.helpCenterArticleProposals
			.where("workspaceId", "=", authData.workspaceId)
			.where("createdByUserId", "=", authData.userId)
			.where("status", "=", "DRAFT")
			.orderBy("updatedAt", "desc")
			.related("baseVersion")
	}),

	notificationInbox: defineQuery(
		notificationFilterSchema.extend({
			limit: z
				.number()
				.int()
				.min(1)
				.max(NOTIFICATION_INBOX_MAX_LIMIT)
				.optional(),
		}),
		({ ctx: authData, args }) => {
			const q = builder.notifications
				.where("userId", authData.userId)
				.where("workspaceId", authData.workspaceId)
			// Rows show the live thread, customer and company logo, not the snapshot taken at fan-out.
			// Primary-key lookups only: 44ms -> ~55ms and 244 rows read for 50 rows on the local replica.
			return whereNotificationFilter(q, args)
				.orderBy("createdAt", "desc")
				.limit(args.limit ?? 50)
				.related("actor", (a) => a.one())
				.related("thread", (t) =>
					t
						.one()
						.related("contact", (c) => c.one())
						.related("company", (c) =>
							c.one().related("companyMeta", (m) => m.one()),
						),
				)
		},
	),

	// No count aggregate: the badge caps at "99+", and an explicit order keeps SQLite on the index.
	notificationUnread: defineQuery(({ ctx: authData }) => {
		return builder.notifications
			.where("userId", authData.userId)
			.where("workspaceId", authData.workspaceId)
			.where("readAt", "IS", null)
			.where("archivedAt", "IS", null)
			.orderBy("createdAt", "desc")
			.limit(100)
	}),

	// Own query, not a filter on notificationUnread: mentions past its newest 100 would go uncounted.
	notificationUnreadMentions: defineQuery(({ ctx: authData }) => {
		return builder.notifications
			.where("userId", authData.userId)
			.where("workspaceId", authData.workspaceId)
			.where("reason", "mentioned")
			.where("readAt", "IS", null)
			.where("archivedAt", "IS", null)
			.orderBy("createdAt", "desc")
			.limit(100)
	}),

	// Unused since notificationUnread reads 100 rows; kept so clients from the previous deploy still resolve it.
	notificationUnreadCount: defineQuery(({ ctx: authData }) => {
		return builder.notifications
			.where("userId", authData.userId)
			.where("workspaceId", authData.workspaceId)
			.where("readAt", "IS", null)
			.where("archivedAt", "IS", null)
			.orderBy("createdAt", "desc")
			.limit(100)
	}),

	notificationSubscription: defineQuery(
		z.object({ entityType: z.string(), entityId: z.string() }),
		({ ctx: authData, args }) => {
			return (
				builder.notificationSubscriptions
					.where("userId", authData.userId)
					.where("workspaceId", authData.workspaceId)
					.where("entityType", args.entityType)
					.where("entityId", args.entityId)
					// Zero's default ORDER BY id walks the primary key (409 rows scanned for one); this keeps SQLite on the unique index.
					.orderBy("entityId", "asc")
					.one()
			)
		},
	),

	notificationSubscribers: defineQuery(
		z.object({ entityId: z.string() }),
		({ ctx: authData, args }) => {
			return (
				builder.notificationSubscriptions
					.where("workspaceId", authData.workspaceId)
					.where("entityId", args.entityId)
					.where("isSubscribed", true)
					// Ordering by id made SQLite scan the workspace's subscriptions (503 rows for 2).
					.orderBy("entityId", "asc")
					.limit(50)
					.related("user", (u) => u.one())
			)
		},
	),

	notificationPreference: defineQuery(({ ctx: authData }) => {
		return builder.notificationPreferences
			.where("workspaceId", authData.workspaceId)
			.where("userId", authData.userId)
			.orderBy("userId", "asc")
			.one()
	}),

	changelogs: defineQuery(({ ctx: authData }) => {
		return authorizedChangelogs(authData.workspaceId)
			.where("isDeleted", false)
			.where("archived", false)
			.related("translations")
			.related("changelogTags")
			.orderBy("date", "desc")
	}),

	changelogTags: defineQuery(({ ctx: authData }) => {
		return builder.changelogTags
			.where("workspaceId", authData.workspaceId)
			.orderBy("name", "asc")
	}),

	changelogTagsForChangelog: defineQuery(
		z.string(),
		({ ctx: authData, args: changelogId }) => {
			return builder.changelogTags
				.where("workspaceId", authData.workspaceId)
				.whereExists("changelogs", (c) => c.where("id", "=", changelogId))
		},
	),

	changelogVersions: defineQuery(
		z.object({ changelogId: z.string(), language: z.string() }),
		({ args: { changelogId, language } }) => {
			return builder.changelogVersions
				.where("changelogId", "=", changelogId)
				.where("language", "=", language)
				.related("createdBy")
				.orderBy("createdAt", "desc")
		},
	),

	searchProjects: defineQuery(
		z.object({
			query: z.string(),
			limit: z.number().optional(),
		}),
		({ ctx: authData, args }) => {
			return authorizedProjects(authData.workspaceId)
				.where("name", "ILIKE", `%${args.query.trim().toLowerCase()}%`)
				.where("isDeleted", false)
				.orderBy("createdAt", "desc")
				.limit(args.limit ?? 20)
		},
	),

	searchProjectsForProjectSelector: defineQuery(
		z.object({
			query: z.string(),
			limit: z.number().optional(),
		}),
		({ ctx: authData, args }) => {
			return authorizedProjects(authData.workspaceId)
				.where("name", "ILIKE", `%${args.query.trim().toLowerCase()}%`)
				.where("state", "!=", "canceled")
				.where("state", "!=", "closed")
				.where("isDeleted", false)
				.orderBy("createdAt", "desc")
				.limit(args.limit ?? 20)
		},
	),

	searchIssues: defineQuery(
		z.object({ query: z.string(), limit: z.number().optional() }),
		({ ctx: authData, args }) => {
			return authorizedIssues(authData.workspaceId)
				.where("title", "ILIKE", `%${args.query.trim().toLowerCase()}%`)
				.where("isDeleted", false)
				.orderBy("createdAt", "desc")
				.limit(args.limit ?? 20)
		},
	),

	searchThreads: defineQuery(
		z.object({
			query: z.string(),
			limit: z.number().int().min(1).max(50).optional(),
			excludeId: z.string().optional(),
			contactId: z.string().optional(),
		}),
		({ ctx: authData, args }) => {
			const trimmed = stripLikeWildcards(args.query.trim())
			const searchTerm = `%${trimmed}%`
			let q = authorizedThreads(authData.workspaceId)
				.where("isDeleted", false)
				.where("origin", "!=", "UPVOTE")
				.where("origin", "!=", "LINEAR")
			if (args.contactId) {
				q = q.where("contactId", args.contactId)
			} else {
				// Merge only ever targets one contact's threads, so a call without a
				// contact must match nothing rather than the whole workspace.
				q = q.where("id", "=", "")
			}
			if (trimmed) {
				q = q.where(({ or, cmp, exists }) =>
					or(
						cmp("title", "ILIKE", searchTerm),
						exists("contact", (c) =>
							c
								.where("isDeleted", false)
								.where("workspaceId", authData.workspaceId)
								.where(({ or: orContact, cmp: cmpContact }) =>
									orContact(
										cmpContact("name", "ILIKE", searchTerm),
										cmpContact("email", "ILIKE", searchTerm),
									),
								),
						),
						exists("company", (c) =>
							c
								.where("isDeleted", false)
								.where("workspaceId", authData.workspaceId)
								.where("name", "ILIKE", searchTerm),
						),
					),
				)
			}
			if (args.excludeId) {
				q = q.where("id", "!=", args.excludeId)
			}
			return applyThreadListRelations(
				q.orderBy("lastMessageAt", "desc").limit(args.limit ?? 20),
				authData.workspaceId,
			)
		},
	),

	// Workspace-wide number and title search for the copilot's "@" context picker
	// and the command palette. searchThreads stays contact-scoped because merging
	// targets one contact.
	searchThreadTitles: defineQuery(
		z.object({
			query: z.string(),
			limit: z.number().int().min(1).max(50).optional(),
		}),
		({ ctx: authData, args }) => {
			const trimmed = stripLikeWildcards(args.query.trim())
			const threadNumber = parseThreadNumber(args.query)
			let q = authorizedThreads(authData.workspaceId)
				.where("isDeleted", false)
				.where("origin", "!=", "UPVOTE")
				.where("origin", "!=", "LINEAR")
			if (threadNumber != null) {
				q = q.where(({ or, cmp }) =>
					or(
						cmp("number", "=", threadNumber),
						cmp("title", "ILIKE", `%${trimmed}%`),
					),
				)
			} else if (trimmed) {
				q = q.where("title", "ILIKE", `%${trimmed}%`)
			}
			return q.orderBy("lastMessageAt", "desc").limit(args.limit ?? 10)
		},
	),

	searchContacts: defineQuery(
		z.object({ query: z.string(), limit: z.number().optional() }),
		({ ctx: authData, args }) => {
			const searchTerm = `%${args.query.trim().toLowerCase()}%`
			return authorizedContacts(authData.workspaceId)
				.where(({ or, cmp }) =>
					or(
						cmp("name", "ILIKE", searchTerm),
						cmp("email", "ILIKE", searchTerm),
					),
				)
				.where("isDeleted", false)
				.orderBy("createdAt", "desc")
				.limit(args.limit ?? 20)
		},
	),

	searchTags: defineQuery(
		z.object({ query: z.string(), limit: z.number().optional() }),
		({ ctx: authData, args }) => {
			return authorizedTags(authData.workspaceId)
				.where("name", "ILIKE", `%${args.query.trim().toLowerCase()}%`)
				.where("isDeleted", false)
				.orderBy("createdAt", "desc")
				.limit(args.limit ?? 20)
		},
	),

	searchCompanies: defineQuery(
		z.object({ query: z.string(), limit: z.number().optional() }),
		({ ctx: authData, args }) => {
			return authorizedCompanies(authData.workspaceId)
				.where("name", "ILIKE", `%${args.query.trim().toLowerCase()}%`)
				.where("isDeleted", false)
				.related("companyMeta")
				.orderBy("createdAt", "desc")
				.limit(args.limit ?? 20)
		},
	),

	searchHelpCenterArticles: defineQuery(
		z.object({ query: z.string(), limit: z.number().optional() }),
		({ ctx: authData, args }) => {
			return authorizedHelpCenterArticles(authData.workspaceId)
				.where("title", "ILIKE", `%${args.query.trim().toLowerCase()}%`)
				.where("isDeleted", false)
				.orderBy("createdAt", "desc")
				.limit(args.limit ?? 20)
		},
	),

	roadmapProjects: defineQuery(
		z.object({
			tab: roadmapTabSchema,
			filters: roadmapFiltersSchema.optional(),
			limit: z.number().int().min(1).max(5000).optional(),
			sortOrder: roadmapSortOrderSchema.optional(),
		}),
		({ ctx: authData, args }) => {
			let q = authorizedProjects(authData.workspaceId)
			q = applyProjectTabFilter(q, args.tab)
			if (args.filters) {
				q = applyRoadmapProjectFilters(q, args.filters)
			}
			const ordered = applyRoadmapSort(
				applyProjectRelations(q, excludesRoadmapLabels(args.filters)),
				args.sortOrder ?? "SCORE_DESC",
			)
			return args.limit ? ordered.limit(args.limit) : ordered
		},
	),

	roadmapIssues: defineQuery(
		z.object({
			tab: roadmapTabSchema,
			filters: roadmapFiltersSchema.optional(),
			limit: z.number().int().min(1).max(5000).optional(),
			sortOrder: roadmapSortOrderSchema.optional(),
			scoreDirection: z.enum(["asc", "desc"]).optional(),
		}),
		({ ctx: authData, args }) => {
			let q = authorizedIssues(authData.workspaceId)
			q = applyIssueTabFilter(q, args.tab)
			if (args.filters) {
				q = applyRoadmapIssueFilters(q, args.filters)
			}
			const ordered = applyRoadmapSort(
				applyIssueRelations(q, excludesRoadmapLabels(args.filters)),
				args.sortOrder ?? "SCORE_DESC",
				args.scoreDirection ?? "desc",
			)
			return args.limit ? ordered.limit(args.limit) : ordered
		},
	),

	issueDetail: defineQuery(z.string(), ({ ctx: authData, args: issueId }) => {
		return authorizedIssues(authData.workspaceId)
			.related("feedbacks", (f) =>
				f.where("isDeleted", false).where("workspaceId", authData.workspaceId),
			)
			.where("id", issueId)
			.where("isDeleted", false)
			.related("translations")
			.related("assignee")
			.related("delegate")
			.related("cycle")
			.one()
	}),

	issueByLinearId: defineQuery(
		z.string(),
		({ ctx: authData, args: linearTicketId }) => {
			return authorizedIssues(authData.workspaceId)
				.where("linearTicketId", linearTicketId)
				.where("isDeleted", false)
				.one()
		},
	),

	subIssues: defineQuery(
		z.string(),
		({ ctx: authData, args: parentLinearId }) => {
			return authorizedIssues(authData.workspaceId)
				.where("parentLinearId", parentLinearId)
				.where("isDeleted", false)
				.related("assignee")
				.orderBy("sortOrder", "asc")
				.limit(250)
		},
	),

	projectDetail: defineQuery(
		z.string(),
		({ ctx: authData, args: projectId }) => {
			return authorizedProjects(authData.workspaceId)
				.where("id", projectId)
				.where("isDeleted", false)
				.related("customerNeeds")
				.related("issues", (i) =>
					i
						.where("isDeleted", false)
						.where("status", "!=", "canceled")
						.where("status", "!=", "closed")
						.where("workspaceId", authData.workspaceId)
						.orderBy("status", "desc")
						.related("customerNeeds"),
				)
				.related("linearRoadmaps", (l) =>
					l
						.where("workspaceId", authData.workspaceId)
						.where("isDeleted", false),
				)
				.related("feedbacks", (f) =>
					f
						.where("workspaceId", authData.workspaceId)
						.where("isDeleted", false),
				)
				.related("translations")
				.related("lead")
				.one()
		},
	),

	contactDetails: defineQuery(
		z.string(),
		({ ctx: authData, args: contactId }) => {
			return authorizedContacts(authData.workspaceId)
				.where("id", contactId)
				.where("isDeleted", false)
				.related("company", (c) =>
					c.where("workspaceId", authData.workspaceId).related("companyMeta"),
				)
				.related("companyLinks", (l) =>
					l
						.where("workspaceId", authData.workspaceId)
						.related("company", (c) => c.related("companyMeta")),
				)
				.one()
		},
	),

	companyDetails: defineQuery(
		z.string(),
		({ ctx: authData, args: companyId }) => {
			return authorizedCompanies(authData.workspaceId)
				.where("id", companyId)
				.where("isDeleted", false)
				.related("contacts", (c) => c.where("isDeleted", false))
				.related("contactLinks", (l) =>
					l.related("contact", (c) => c.where("isDeleted", false)),
				)
				.related("companyMeta")
				.one()
		},
	),

	companyRequests: defineQuery(
		z.string(),
		({ ctx: authData, args: companyId }) => {
			return authorizedThreads(authData.workspaceId)
				.where("companyId", companyId)
				.where("isDeleted", false)
				.where("origin", "!=", "UPVOTE")
		},
	),

	companyUpvotes: defineQuery(
		z.string(),
		({ ctx: authData, args: companyId }) => {
			return authorizedThreads(authData.workspaceId)
				.where("companyId", companyId)
				.where("isDeleted", false)
				.where("origin", "=", "UPVOTE")
		},
	),

	contactRequests: defineQuery(
		z.string(),
		({ ctx: authData, args: contactId }) => {
			return authorizedThreads(authData.workspaceId)
				.where("contactId", contactId)
				.where("isDeleted", false)
				.where("origin", "!=", "UPVOTE")
		},
	),

	contactUpvotes: defineQuery(
		z.string(),
		({ ctx: authData, args: contactId }) => {
			return authorizedThreads(authData.workspaceId)
				.where("contactId", contactId)
				.where("isDeleted", false)
				.where("origin", "=", "UPVOTE")
		},
	),

	companyUpvotedItems: defineQuery(
		z.string(),
		({ ctx: authData, args: companyId }) => {
			return authorizedThreads(authData.workspaceId)
				.where("companyId", companyId)
				.where("isDeleted", false)
				.where("origin", "=", "UPVOTE")
				.related("projects")
				.related("issues")
		},
	),

	projectsForContact: defineQuery(
		z.string(),
		({ ctx: authData, args: contactId }) => {
			return authorizedProjects(authData.workspaceId)
				.where("isDeleted", false)
				.whereExists(
					"feedbacks",
					(f) =>
						f
							.where("workspaceId", authData.workspaceId)
							.where("contactId", contactId)
							.where("isDeleted", false),
					{ flip: true },
				)
		},
	),

	issuesForContact: defineQuery(
		z.string(),
		({ ctx: authData, args: contactId }) => {
			return authorizedIssues(authData.workspaceId)
				.where("isDeleted", false)
				.whereExists(
					"feedbacks",
					(f) =>
						f
							.where("workspaceId", authData.workspaceId)
							.where("contactId", contactId)
							.where("isDeleted", false),
					{ flip: true },
				)
		},
	),

	projectsForCompany: defineQuery(
		z.string(),
		({ ctx: authData, args: companyId }) => {
			return authorizedProjects(authData.workspaceId)
				.where("isDeleted", false)
				.whereExists(
					"feedbacks",
					(f) =>
						f
							.where("workspaceId", authData.workspaceId)
							.where("companyId", companyId)
							.where("isDeleted", false),
					{ flip: true },
				)
				.related("feedbacks", (f) =>
					f
						.where("workspaceId", authData.workspaceId)
						.where("companyId", companyId)
						.where("isDeleted", false),
				)
		},
	),

	issuesForCompany: defineQuery(
		z.string(),
		({ ctx: authData, args: companyId }) => {
			return authorizedIssues(authData.workspaceId)
				.where("isDeleted", false)
				.whereExists(
					"feedbacks",
					(f) =>
						f
							.where("workspaceId", authData.workspaceId)
							.where("companyId", companyId)
							.where("isDeleted", false),
					{ flip: true },
				)
				.related("feedbacks", (f) =>
					f
						.where("workspaceId", authData.workspaceId)
						.where("companyId", companyId)
						.where("isDeleted", false),
				)
		},
	),

	tagDetail: defineQuery(z.string(), ({ ctx: authData, args: tagId }) => {
		return authorizedTags(authData.workspaceId)
			.where("id", tagId)
			.where("isDeleted", false)
			.one()
	}),

	helpCenterArticles: defineQuery(({ ctx: authData }) => {
		return authorizedHelpCenterArticles(authData.workspaceId).where(
			"isDeleted",
			false,
		)
	}),

	// Lets a writer resolve the existing row before upserting, so a restore never
	// inserts a second translation row for the same language.
	helpCenterArticleTranslation: defineQuery(
		z.object({ articleId: z.string(), language: z.string() }),
		({ ctx: authData, args }) => {
			return builder.helpCenterArticleTranslations
				.where("workspaceId", authData.workspaceId)
				.where("helpCenterArticleId", "=", args.articleId)
				.where("language", "=", args.language)
				.one()
		},
	),

	changelogTranslation: defineQuery(
		z.object({ changelogId: z.string(), language: z.string() }),
		({ ctx: authData, args }) => {
			return builder.changelogTranslations
				.where("workspaceId", authData.workspaceId)
				.where("changelogId", "=", args.changelogId)
				.where("language", "=", args.language)
				.one()
		},
	),

	companiesWithContacts: defineQuery(
		z.object({
			filters: companiesFiltersSchema.optional(),
			search: z.string().optional(),
			limit: z.number().optional(),
			sortColumn: companiesSortColumnSchema.optional(),
			sortOrder: sortByColumnOrder.optional(),
			startAfter: companiesCursorSchema.optional(),
		}),
		({ ctx: authData, args }) => {
			const { filters, search, limit, sortColumn, sortOrder, startAfter } = args
			let q = companiesSelectionQuery(authData.workspaceId, {
				filters,
				search,
			})

			const order = sortOrder ?? "asc"
			q = q
				.related("companyMeta")
				.orderBy(sortColumn ?? "name", order)
				.orderBy("id", order)

			if (startAfter) {
				q = q.start(startAfter)
			}
			if (limit !== undefined) {
				q = q.limit(limit)
			}
			return q
		},
	),
	memberships: defineQuery(({ ctx: authData }) => {
		return builder.memberships
			.where("workspaceId", authData.workspaceId)
			.related("user")
			.orderBy("createdAt", "desc")
	}),

	membershipByUserId: defineQuery(
		z.object({ userId: z.string() }),
		({ ctx: authData, args }) => {
			return builder.memberships
				.where("workspaceId", authData.workspaceId)
				.where("userId", args.userId)
				.related("user")
				.one()
		},
	),

	invitedUsers: defineQuery(({ ctx: authData }) => {
		return builder.workspaceInvites
			.where("workspaceId", authData.workspaceId)
			.where("state", "PENDING")
			.orderBy("createdAt", "desc")
	}),

	allowedDomains: defineQuery(({ ctx: authData }) => {
		return builder.allowedEmailDomains
			.where("workspaceId", authData.workspaceId)
			.orderBy("createdAt", "desc")
	}),

	// Public API v2: read-only sync. Mutations (create/revoke) go through
	// tRPC `workspace.createApiKeyV2` / `workspace.revokeApiKeyV2`.
	apiKeysV2: defineQuery(({ ctx: authData }) => {
		return authorizedApiKeysV2(authData.workspaceId)
			.related("createdBy")
			.orderBy("createdAt", "desc")
	}),

	webhooks: defineQuery(({ ctx: authData }) => {
		return authorizedWebhooks(authData.workspaceId)
			.related("createdBy")
			.orderBy("createdAt", "desc")
	}),

	// Recent delivery audit log for a webhook. Capped at 200 rows to
	// bound bandwidth on chatty workspaces — older history stays in
	// Postgres until the 14-day cron prune. The settings UI uses this
	// for the "recent activity" panel; if/when we need full history,
	// we'll add a tRPC paginated endpoint instead of unbounding Zero.
	webhookDeliveries: defineQuery(
		z.object({ webhookId: z.string() }),
		({ ctx: authData, args: { webhookId } }) => {
			return builder.webhookDeliveries
				.where("webhookId", webhookId)
				.whereExists("webhook", (q) =>
					q.where("workspaceId", authData.workspaceId),
				)
				.orderBy("createdAt", "desc")
				.limit(200)
		},
	),

	widgetSettings: defineQuery(({ ctx: authData }) => {
		return builder.widgetSettings
			.where("workspaceId", authData.workspaceId)
			.one()
	}),
	/** One Slack workspace's channels for the Channels page, only loaded there. */
	slackChannels: defineQuery(
		z.object({ slackConnectSettingsId: z.string() }),
		({ ctx: authData, args }) => {
			return builder.slackChannels
				.where("workspaceId", authData.workspaceId)
				.where("slackConnectSettingsId", args.slackConnectSettingsId)
				.where("isMember", true)
				.orderBy("name", "asc")
				.orderBy("id", "asc")
				.related("tag", (q) => q.related("tagGroup"))
				.related("assignee")
		},
	),

	/** Channels for the inbox's Slack channel filter, searched in the query. */
	searchSlackChannels: defineQuery(
		z.object({
			search: z.string().max(100),
			limit: z.number().int().min(1).max(200),
		}),
		({ ctx: authData, args }) => {
			let query = builder.slackChannels
				.where("workspaceId", authData.workspaceId)
				.where("isMember", true)
			const search = args.search.trim().toLowerCase()
			if (search) query = query.where("name", "ILIKE", `%${search}%`)
			return query.orderBy("name", "asc").orderBy("id", "asc").limit(args.limit)
		},
	),

	/** Names for the Slack channel filter chips. */
	slackChannelsByChannelIds: defineQuery(
		z.object({ channelIds: z.array(z.string()).max(500) }),
		({ ctx: authData, args }) => {
			return builder.slackChannels
				.where("workspaceId", authData.workspaceId)
				.where("channelId", "IN", args.channelIds)
		},
	),

	/** The stored channel a Slack thread came from, to tell which Slack workspace it belongs to. */
	slackChannelByChannelId: defineQuery(
		z.object({ channelId: z.string() }),
		({ ctx: authData, args }) => {
			return builder.slackChannels
				.where("workspaceId", authData.workspaceId)
				.where("channelId", args.channelId)
				.orderBy("createdAt", "asc")
				.orderBy("id", "asc")
				.one()
		},
	),

	/** Public channels the bot is not in, paged and searched in the query, since a community can have thousands. */
	slackChannelsNotJoined: defineQuery(
		z.object({
			slackConnectSettingsId: z.string(),
			search: z.string().max(100),
			limit: z.number().int().min(1).max(1000),
			order: z.enum(["asc", "desc"]),
		}),
		({ ctx: authData, args }) => {
			let query = builder.slackChannels
				.where("workspaceId", authData.workspaceId)
				.where("slackConnectSettingsId", args.slackConnectSettingsId)
				.where("isMember", false)
				.where("isPrivate", false)
			const search = args.search.trim().toLowerCase()
			if (search) query = query.where("name", "ILIKE", `%${search}%`)
			return query
				.orderBy("name", args.order)
				.orderBy("id", args.order)
				.limit(args.limit)
		},
	),

	/** The notification pickers: joined channels, plus public ones the bot joins when picked. */
	slackChannelPicker: defineQuery(
		z.object({
			slackConnectSettingsId: z.string(),
			search: z.string().max(100),
			limit: z.number().int().min(1).max(200),
		}),
		({ ctx: authData, args }) => {
			let query = builder.slackChannels
				.where("workspaceId", authData.workspaceId)
				.where("slackConnectSettingsId", args.slackConnectSettingsId)
				.where(({ or, cmp }) =>
					or(cmp("isMember", true), cmp("isPrivate", false)),
				)
			const search = args.search.trim().toLowerCase()
			if (search) query = query.where("name", "ILIKE", `%${search}%`)
			return query.orderBy("name", "asc").orderBy("id", "asc").limit(args.limit)
		},
	),

	tagGroups: defineQuery(({ ctx: authData }) => {
		return authorizedTagGroups(authData.workspaceId).where("isDeleted", false)
	}),

	widgetLinks: defineQuery(({ ctx: authData }) => {
		return builder.widgetLinks
			.where("workspaceId", authData.workspaceId)
			.orderBy("createdAt", "desc")
			.limit(5)
	}),
	widgetContactAvatars: defineQuery(({ ctx: authData }) => {
		return builder.widgetContactAvatars
			.whereExists("widgetSettings", (q) =>
				q.where("workspaceId", authData.workspaceId),
			)
			.whereExists("membership", (q) => q.where("role", "!=", "VIEWER"))
			.related("membership", (m) => m.related("user"))
			.orderBy("order", "asc")
	}),
	slackUsers: defineQuery(({ ctx: authData }) => {
		return authorizedSlackUsers(authData.workspaceId).where("isDeleted", false)
	}),

	// One query per open thread; the timeline groups the rows by message.
	threadMessageReactions: defineQuery(
		z.string(),
		({ ctx: authData, args: threadId }) => {
			return builder.messageReactions
				.where("workspaceId", authData.workspaceId)
				.where("threadId", threadId)
				.orderBy("createdAt", "asc")
				.orderBy("id", "asc")
				.limit(1000)
		},
	),

	linearUsers: defineQuery(({ ctx: authData }) => {
		return authorizedLinearUsers(authData.workspaceId)
	}),

	// Thread sidebar: HTTP sections render components; MCP sections list as
	// logo rows that open the AI agent. Settings still reads every type off
	// the workspace query.
	sidebarSections: defineQuery(({ ctx: authData }) => {
		return authorizedSidebarSections(authData.workspaceId)
			.where("isDeleted", false)
			.orderBy("order", "asc")
	}),

	recentThreads: defineQuery(
		z.object({
			companyId: z.string(),
			excludeThreadId: z.string(),
			limit: z.number(),
		}),
		({ ctx: authData, args }) => {
			return authorizedThreads(authData.workspaceId)
				.where("isDeleted", false)
				.where("companyId", args.companyId)
				.where("id", "!=", args.excludeThreadId)
				.where("origin", "!=", "LINEAR")
				.where("origin", "!=", "UPVOTE")
				.orderBy("createdAt", "desc")
				.related("contact", (c) =>
					c
						.where("workspaceId", authData.workspaceId)
						.where("isDeleted", false),
				)
				.limit(args.limit)
		},
	),

	// Ids are random, so id order reads a fair sample straight off the company
	// index; ordering by lastMessageAt sorts every thread (66k on prod's largest).
	companyContactThreadSample: defineQuery(
		z.string(),
		({ ctx: authData, args: companyId }) => {
			return authorizedThreads(authData.workspaceId)
				.where("isDeleted", false)
				.where("companyId", companyId)
				.where("contactId", "IS NOT", null)
				.limit(50)
		},
	),

	threadWithFullDetails: defineQuery(
		z.object({
			threadId: z.string(),
			limit: z
				.number()
				.int()
				.positive()
				.max(feedbackDetailMessageLimitMax)
				.optional(),
		}),
		({ ctx: authData, args: { threadId, limit } }) => {
			// Each capped relation fetches one sentinel row past its display
			// limit, so the oldest row is not guaranteed displayable.
			const { messageLimit, activityLimit } = threadDetailRelationLimits(limit)
			return authorizedThreads(authData.workspaceId)
				.where("id", threadId)
				.related("company", (c) =>
					/// TODO: properly fix the issue that companies
					c
						.where("workspaceId", authData.workspaceId)
						.where("isDeleted", false)
						.related("companyMeta"),
				)
				.related("contact", (c) =>
					c
						.where("workspaceId", authData.workspaceId)
						.where("isDeleted", false),
				)
				.related("emails", (e) =>
					e
						.where("workspaceId", authData.workspaceId)
						.where("isDeleted", false)
						.orderBy("createdAt", "desc")
						.orderBy("id", "desc")
						.limit(messageLimit + 1)
						.related("content", (c) =>
							c.where("workspaceId", authData.workspaceId),
						),
				)
				.related("slackMessages", (s) =>
					s
						.where("workspaceId", authData.workspaceId)
						.where("isDeleted", false)
						.orderBy("createdAt", "desc")
						.orderBy("id", "desc")
						.limit(messageLimit + 1),
				)
				.related("chatMessages", (s) =>
					s
						.where("workspaceId", authData.workspaceId)
						.where("isDeleted", false)
						.orderBy("createdAt", "desc")
						.orderBy("id", "desc")
						.limit(messageLimit + 1),
				)
				.related("threadMessages", (t) =>
					t
						.where("workspaceId", authData.workspaceId)
						.where("isDeleted", false)
						.orderBy("createdAt", "desc")
						.orderBy("id", "desc")
						.limit(messageLimit + 1),
				)
				.related("issues", (i) =>
					i
						.where("workspaceId", authData.workspaceId)
						.where("isDeleted", false)
						.related("customerNeeds", (cn) => cn.where("feedbackId", threadId)),
				)
				.related("projects", (p) =>
					p
						.where("workspaceId", authData.workspaceId)
						.where("isDeleted", false)
						.related("customerNeeds", (cn) => cn.where("feedbackId", threadId)),
				)
				.related("tags", (t) =>
					t
						.where("workspaceId", authData.workspaceId)
						.where("isDeleted", false),
				)
				.related("comments", (c) =>
					c
						.where("workspaceId", authData.workspaceId)
						.where("isDeleted", false)
						.orderBy("createdAt", "desc")
						.orderBy("id", "desc")
						.limit(messageLimit + 1),
				)
				.related("liveChatSession", (l) =>
					l
						.where("workspaceId", authData.workspaceId)
						.related("messages", (q) =>
							q
								.where("isDeleted", false)
								.orderBy("createdAt", "desc")
								.orderBy("id", "desc")
								.limit(messageLimit + 1),
						),
				)
				.related("threadActivities", (te) =>
					te
						.where("isDeleted", false)
						.orderBy("createdAt", "desc")
						.orderBy("id", "desc")
						.limit(activityLimit + 1),
				)
				.related("reporter")
				.related("assignee")
				.one()
		},
	),

	// One fixed-cursor history page per relation, older than `before`. Must
	// root at ONE thread: Skip bounds apply globally, not per parent row.
	threadOlderMessages: defineQuery(
		z.object({
			threadId: z.string(),
			before: z.object({
				createdAt: z.number(),
				id: z.string(),
			}),
			pageSize: z.number().int().positive().max(200).optional(),
		}),
		({ ctx: authData, args: { threadId, before, pageSize } }) => {
			const limit = (pageSize ?? feedbackOlderMessagesPageSize) + 1
			return authorizedThreads(authData.workspaceId)
				.where("id", threadId)
				.where("isDeleted", false)
				.related("emails", (e) =>
					e
						.where("workspaceId", authData.workspaceId)
						.where("isDeleted", false)
						.orderBy("createdAt", "desc")
						.orderBy("id", "desc")
						.start(before, { inclusive: false })
						.limit(limit)
						.related("content", (c) =>
							c.where("workspaceId", authData.workspaceId),
						),
				)
				.related("slackMessages", (s) =>
					s
						.where("workspaceId", authData.workspaceId)
						.where("isDeleted", false)
						.orderBy("createdAt", "desc")
						.orderBy("id", "desc")
						.start(before, { inclusive: false })
						.limit(limit),
				)
				.related("chatMessages", (s) =>
					s
						.where("workspaceId", authData.workspaceId)
						.where("isDeleted", false)
						.orderBy("createdAt", "desc")
						.orderBy("id", "desc")
						.start(before, { inclusive: false })
						.limit(limit),
				)
				.related("threadMessages", (t) =>
					t
						.where("workspaceId", authData.workspaceId)
						.where("isDeleted", false)
						.orderBy("createdAt", "desc")
						.orderBy("id", "desc")
						.start(before, { inclusive: false })
						.limit(limit),
				)
				.related("comments", (c) =>
					c
						.where("workspaceId", authData.workspaceId)
						.where("isDeleted", false)
						.orderBy("createdAt", "desc")
						.orderBy("id", "desc")
						.start(before, { inclusive: false })
						.limit(limit),
				)
				.related("liveChatSession", (l) =>
					l
						.where("workspaceId", authData.workspaceId)
						.related("messages", (q) =>
							q
								.where("isDeleted", false)
								.orderBy("createdAt", "desc")
								.orderBy("id", "desc")
								.start(before, { inclusive: false })
								.limit(limit),
						),
				)
				.related("threadActivities", (te) =>
					te
						.where("isDeleted", false)
						.orderBy("createdAt", "desc")
						.orderBy("id", "desc")
						.start(before, { inclusive: false })
						.limit(limit),
				)
				.one()
		},
	),

	// Oldest rows only, for consumers that need the true start of the thread
	// (issue body from the first inbound email, Slack root permalink).
	// Open issue, project and tag suggestions for one thread, at most twelve
	// rows. The sidebar drops closed, linked and applied targets on read, so a
	// stale row never shows.
	threadSuggestions: defineQuery(
		z.object({ threadId: z.string() }),
		({ ctx: authData, args: { threadId } }) => {
			return builder.suggestions
				.where("workspaceId", authData.workspaceId)
				.where("threadId", threadId)
				.where("kind", "IN", ["link_issue", "link_project", "thread_tag"])
				.where("dismissedAt", "IS", null)
				.related("targetIssue", (q) =>
					q.where("workspaceId", authData.workspaceId),
				)
				.related("targetProject", (q) =>
					q.where("workspaceId", authData.workspaceId),
				)
				.related("targetTag", (q) =>
					q.where("workspaceId", authData.workspaceId),
				)
				.orderBy("score", "desc")
				.limit(12)
		},
	),

	threadOldestMessages: defineQuery(
		z.object({
			threadId: z.string(),
		}),
		({ ctx: authData, args: { threadId } }) => {
			return authorizedThreads(authData.workspaceId)
				.where("id", threadId)
				.where("isDeleted", false)
				.related("emails", (e) =>
					e
						.where("workspaceId", authData.workspaceId)
						.where("isDeleted", false)
						.where("type", "INBOUND")
						.orderBy("createdAt", "asc")
						.orderBy("id", "asc")
						.limit(1)
						.related("content", (c) =>
							c.where("workspaceId", authData.workspaceId),
						),
				)
				.related("slackMessages", (s) =>
					s
						.where("workspaceId", authData.workspaceId)
						.where("isDeleted", false)
						.orderBy("createdAt", "asc")
						.orderBy("id", "asc")
						.limit(10),
				)
				.one()
		},
	),

	findSearchedThreads: defineQuery(
		z.object({
			threadIds: z.array(z.string()),
		}),
		({ ctx: authData, args: { threadIds } }) => {
			return authorizedThreads(authData.workspaceId)
				.where("isDeleted", false)
				.where("id", "IN", threadIds)
				.related("company", (q) =>
					q
						.where("workspaceId", authData.workspaceId)
						.where("isDeleted", false)
						.related("companyMeta"),
				)
				.related("contact", (q) => q.where("isDeleted", false))
				.related("assignee")
				.related("emails", (e) =>
					e
						.where("workspaceId", authData.workspaceId)
						.where("isDeleted", false)
						// The preview skips auto-replies, so one row only suffices when
						// the relation drops them; a null source is a legacy row.
						.where(({ or, cmp }) =>
							or(cmp("source", "IS", null), cmp("source", "!=", "AUTO_REPLY")),
						)
						.orderBy("createdAt", "desc")
						.orderBy("id", "desc")
						.limit(1),
				)
				.related("slackMessages", (s) =>
					s
						.where("workspaceId", authData.workspaceId)
						.where("isDeleted", false)
						.orderBy("createdAt", "desc")
						.orderBy("id", "desc")
						.limit(1),
				)
				.related("chatMessages", (s) =>
					s
						.where("workspaceId", authData.workspaceId)
						.where("isDeleted", false)
						.orderBy("createdAt", "desc")
						.orderBy("id", "desc")
						.limit(1),
				)
				.related("threadMessages", (t) =>
					t
						.where("workspaceId", authData.workspaceId)
						.where("isDeleted", false)
						.orderBy("createdAt", "desc")
						.orderBy("id", "desc")
						.limit(1),
				)
				.related("liveChatSession", (l) =>
					l
						.where("workspaceId", authData.workspaceId)
						.related("messages", (q) =>
							q.where("isDeleted", false).orderBy("createdAt", "desc").limit(1),
						)
						.orderBy("createdAt", "desc")
						.orderBy("id", "desc")
						.limit(1),
				)
		},
	),

	teamMembers: defineQuery(({ ctx: authData }) => {
		return authorizedMemberships(authData.workspaceId).related("user")
	}),

	pendingProjects: defineQuery(
		z
			.object({ filters: roadmapFiltersSchema.optional() })
			.optional()
			.default({}),
		({ ctx: authData, args }) => {
			let q = authorizedProjects(authData.workspaceId)
				.where("isVisible", "IS", null)
				.where("isDeleted", false)
				.where("state", "!=", "canceled")
				.where("state", "!=", "completed")
				.where("state", "!=", "closed")
			if (args?.filters) {
				q = applyRoadmapProjectFilters(q, args.filters)
			}
			return q
		},
	),

	blockedSenders: defineQuery(({ ctx: authData }) => {
		return authorizedBlockedSenders(authData.workspaceId)
	}),

	forwardedEmailIgnoreSenders: defineQuery(({ ctx: authData }) => {
		return authorizedForwardedEmailIgnoreSenders(authData.workspaceId)
	}),

	activeLiveChatSessions: defineQuery(({ ctx: authData }) => {
		return (
			builder.liveChatSessions
				.where("workspaceId", authData.workspaceId)
				.where("isFinished", false)
				// The notification watcher only compares the newest messages of each session.
				.related("messages", (q) =>
					q.where("isDeleted", false).orderBy("createdAt", "desc").limit(5),
				)
				.related("thread")
				.limit(50)
		)
	}),
	snippets: defineQuery(({ ctx: authData }) => {
		return builder.snippets
			.where("workspaceId", authData.workspaceId)
			.where("isDeleted", false)
	}),

	snippetFolders: defineQuery(({ ctx: authData }) => {
		return builder.snippetFolders
			.where("workspaceId", authData.workspaceId)
			.where("isDeleted", false)
			.orderBy("name", "asc")
	}),

	projectByLinearProjectId: defineQuery(
		z.object({ linearProjectId: z.string() }),
		({ ctx: authData, args: { linearProjectId } }) => {
			return authorizedProjects(authData.workspaceId)
				.where("linearProjectId", linearProjectId)
				.where("isDeleted", false)
				.one()
		},
	),

	companiesWithMeta: defineQuery(({ ctx: authData }) => {
		return authorizedCompanies(authData.workspaceId)
			.where("isDeleted", false)
			.related("companyMeta")
	}),

	widgetThreads: defineQuery(({ ctx: authData }) => {
		return authorizedThreads(authData.workspaceId)
			.where("isDeleted", false)
			.where("origin", "=", "WIDGET")
			.limit(1)
	}),

	projectUpvoters: defineQuery(
		z.object({
			projectId: z.string().optional(),
			contactId: z.string().optional(),
			companyId: z.string().optional(),
		}),
		({ ctx: authData, args }) => {
			let q = builder.upvotes
				.where("workspaceId", authData.workspaceId)
				.where("isDeleted", false)
			if (args.projectId) {
				q = q.where("projectId", args.projectId)
			}
			if (args.contactId) {
				q = q.where("contactId", args.contactId)
			}
			if (args.companyId) {
				const companyId = args.companyId
				q = q.whereExists("contact", (c) => c.where("companyId", companyId))
			}
			if (!args.projectId && !args.contactId && !args.companyId) {
				q = q.where("id", "IS", null)
			}
			return q
				.related("contact", (c) =>
					c
						.where("isDeleted", false)
						.related("company", (co) => co.related("companyMeta")),
				)
				.related("project")
				.orderBy("createdAt", "desc")
		},
	),

	issueUpvoters: defineQuery(
		z.object({
			issueId: z.string().optional(),
			contactId: z.string().optional(),
			companyId: z.string().optional(),
		}),
		({ ctx: authData, args }) => {
			let q = builder.issueUpvotes
				.where("workspaceId", authData.workspaceId)
				.where("isDeleted", false)
			if (args.issueId) {
				q = q.where("issueId", args.issueId)
			}
			if (args.contactId) {
				q = q.where("contactId", args.contactId)
			}
			if (args.companyId) {
				const companyId = args.companyId
				q = q.whereExists("contact", (c) => c.where("companyId", companyId))
			}
			if (!args.issueId && !args.contactId && !args.companyId) {
				q = q.where("id", "IS", null)
			}
			return q
				.related("contact", (c) =>
					c
						.where("isDeleted", false)
						.related("company", (co) => co.related("companyMeta")),
				)
				.related("issue")
				.orderBy("createdAt", "desc")
		},
	),

	customViews: defineQuery(({ ctx: authData }) => {
		return builder.customViews
			.where("workspaceId", authData.workspaceId)
			.where("isDeleted", false)
			.where("surface", "threads")
			.orderBy("sortOrder", "asc")
			.orderBy("createdAt", "asc")
	}),

	linearLabels: defineQuery(({ ctx: authData }) => {
		return builder.linearLabels
			.where("workspaceId", authData.workspaceId)
			.where("isDeleted", false)
			.orderBy("name", "asc")
	}),

	roadmapViews: defineQuery(({ ctx: authData }) => {
		return builder.customViews
			.where("workspaceId", authData.workspaceId)
			.where("isDeleted", false)
			.where("surface", "roadmap")
			.orderBy("sortOrder", "asc")
			.orderBy("createdAt", "asc")
	}),

	codingAgentRunsByIssue: defineQuery(
		z.string(),
		({ ctx: authData, args: issueId }) => {
			return builder.codingAgentRuns
				.where("workspaceId", authData.workspaceId)
				.where("issueId", issueId)
				.orderBy("createdAt", "desc")
		},
	),
})

export type TeamMember = Membership & {
	user?: User
}

export type FeedbackWithRelations = Row<
	ReturnType<typeof applyThreadListRelations>
>

export type FeedbackSearchResult = Row<
	ReturnType<typeof queries.findSearchedThreads>
>

export type AgentCodeFix = Row<ReturnType<typeof queries.agentCodeFixes>>

export type ThreadOlderMessagesRow = NonNullable<
	Row<ReturnType<typeof queries.threadOlderMessages>>
>

export type ThreadOldestMessagesRow = NonNullable<
	Row<ReturnType<typeof queries.threadOldestMessages>>
>

export type AgentChatRow = NonNullable<
	Row<ReturnType<typeof queries.agentChatsNeedingPerson>>
>

function authorizedMemberships(workspaceId: string) {
	return builder.memberships.where("workspaceId", workspaceId)
}

function authorizedThreads(workspaceId: string) {
	return builder.feedbacks.where("workspaceId", workspaceId)
}

export const inboxSelectionSchema = z.object({
	tab: inboxStateSchema.optional(),
	filters: threadFiltersSchema.optional(),
	stateScope: stateScopeSchema.optional(),
	nowMs: z.number().optional(),
})

export type InboxSelection = z.infer<typeof inboxSelectionSchema>

function inboxThreads(workspaceId: string) {
	return authorizedThreads(workspaceId)
		.where("isDeleted", false)
		.where(({ or, cmp, exists }) =>
			or(
				cmp("origin", "!=", "UPVOTE"),
				exists("emails", (e) => e.where("type", "INBOUND")),
			),
		)
}

function filterInboxThreads(
	query: Query<"feedbacks", Schema>,
	auth: { workspaceId: string; userId: string },
	{ tab, filters, stateScope, nowMs }: InboxSelection,
) {
	let q = query
	if (tab !== undefined && tab !== "custom-view") {
		q = applyTabFilter(q, auth.workspaceId, tab, auth.userId, nowMs, stateScope)
	}
	if (shouldApplyStateScope(tab, stateScope)) {
		q = applyStateScope(q, stateScope)
	}
	if (filters !== undefined) {
		q = applyThreadFilters(q, filters)
	}
	return q
}

/** The inbox list's rows for a tab, scope and filters, unpaged; select-all runs it on the server to reach past the synced window. */
export function inboxSelectionQuery(
	auth: { workspaceId: string; userId: string },
	selection: InboxSelection,
) {
	return filterInboxThreads(inboxThreads(auth.workspaceId), auth, selection)
}

/** The companies list's rows for a search and filters, unpaged; select-all runs it on the server to reach past the synced window. */
/** The contacts the People list shows for a search and filters, shared with select-all. */
export function contactsSelectionQuery(
	workspaceId: string,
	{ filters, search }: ContactsSelection,
) {
	let q = builder.contacts
		.where("workspaceId", workspaceId)
		.where("isDeleted", false)
	if (filters) {
		q = applyContactsFilters(q, filters)
	}
	const term = stripLikeWildcards(search?.trim().toLowerCase() ?? "")
	if (term) {
		q = q.where(({ or, cmp }) =>
			or(cmp("name", "ILIKE", `%${term}%`), cmp("email", "ILIKE", `%${term}%`)),
		)
	}
	return q
}

export function companiesSelectionQuery(
	workspaceId: string,
	{ filters, search }: CompaniesSelection,
) {
	let q = builder.companies
		.where("workspaceId", workspaceId)
		.where("isDeleted", false)
	if (filters) {
		q = applyCompaniesFilters(q, filters)
	}
	if (search && search.trim().length > 0) {
		const term = `%${search.trim().toLowerCase()}%`
		q = q.where(({ or, cmp }) =>
			or(cmp("name", "ILIKE", term), cmp("domain", "ILIKE", term)),
		)
	}
	return q
}

// The term goes straight into an ILIKE pattern, so a typed "%" would match
// everything. Escape semantics differ across the engines Zero runs this on, so
// drop the wildcards instead of escaping them.
function stripLikeWildcards(term: string): string {
	return term.replace(/[%_\\]/g, "")
}

function authorizedHelpCenterGroups(workspaceId: string) {
	return builder.helpCenterGroups.where("workspaceId", workspaceId)
}

function authorizedProjects(workspaceId: string) {
	return builder.projects.where("workspaceId", workspaceId)
}

function authorizedIssues(workspaceId: string) {
	return builder.issues.where("workspaceId", workspaceId)
}

function authorizedContacts(workspaceId: string) {
	return builder.contacts.where("workspaceId", workspaceId)
}

function authorizedTags(workspaceId: string) {
	return builder.tags.where("workspaceId", workspaceId)
}

function authorizedCompanies(workspaceId: string) {
	return builder.companies.where("workspaceId", workspaceId)
}

function authorizedHelpCenterArticles(workspaceId: string) {
	return builder.helpCenterArticles.where("workspaceId", workspaceId)
}

function authorizedChangelogs(workspaceId: string) {
	return builder.changelogs.where("workspaceId", workspaceId)
}

function authorizedSidebarSections(workspaceId: string) {
	return builder.sidebarSections.where("workspaceId", workspaceId)
}

function authorizedSlackUsers(workspaceId: string) {
	return builder.slackUsers.where("workspaceId", workspaceId)
}

function authorizedLinearUsers(workspaceId: string) {
	return builder.linearUsers.where("workspaceId", workspaceId)
}

function authorizedApiKeysV2(workspaceId: string) {
	return builder.apiKeys.where("workspaceId", workspaceId)
}

function authorizedWebhooks(workspaceId: string) {
	return builder.webhooks.where("workspaceId", workspaceId)
}

function authorizedTagGroups(workspaceId: string) {
	return builder.tagGroups.where("workspaceId", workspaceId)
}

function authorizedBlockedSenders(workspaceId: string) {
	return builder.blockedSender.where("workspaceId", workspaceId)
}

function authorizedForwardedEmailIgnoreSenders(workspaceId: string) {
	return builder.forwardedEmailIgnoreSender.where("workspaceId", workspaceId)
}

// Sessions the AI agent ran for a customer. A sidebar copilot session hangs off
// the same thread with a userId set, and resolution billing ignores those too.
function agentSessions(
	s: Query<"aiChatSessions", Schema>,
	workspaceId: string,
) {
	// No AiChatSession index leads with threadId, so without the workspace every
	// per-thread lookup skip-scans all workspaces.
	return s
		.where("workspaceId", workspaceId)
		.where("isDeleted", false)
		.where("userId", "IS", null)
}

export const AGENT_CHAT_SIGNAL_EVENTS = [
	"ai_handoff",
	"ai_reply_skipped",
	"ai_chat_feedback",
] as const

// Activities that can mean the agent needed a person. Which ones actually do
// (a no-help skip, a thumbs down) is read from the payload on the client.
function agentChatSignals(
	a: Query<"threadActivities", Schema>,
	workspaceId: string,
) {
	return a
		.where("workspaceId", workspaceId)
		.where("isDeleted", false)
		.where("eventType", "IN", [...AGENT_CHAT_SIGNAL_EVENTS])
}

// escalatedAt was not backfilled, so handoffs from before it shipped are only
// visible through the activity they wrote.
function escalatedThreads(q: Query<"feedbacks", Schema>, workspaceId: string) {
	// Flipped so a few hundred handoffs drive the join instead of every thread.
	return q.where((eb) =>
		eb.or(
			eb.exists(
				"aiChatSessions",
				(s) =>
					agentSessions(s, workspaceId).where("escalatedAt", "IS NOT", null),
				{ flip: true },
			),
			eb.exists(
				"threadActivities",
				(a) =>
					a
						.where("workspaceId", workspaceId)
						.where("isDeleted", false)
						.where("eventType", "ai_handoff"),
				{ flip: true },
			),
		),
	)
}

// Open means one of these two: state is never null, and an IN can use the state index where != can't.
const OPEN_STATES = ["NEW", "UNSNOOZED"] as const

// A count ignores row order, so the badge tabs order by the index their filter
// uses. Ordered by id, SQLite walked every unassigned thread to count New.
function orderThreadCount(
	q: Query<"feedbacks", Schema>,
	tab: InboxState | undefined,
) {
	switch (tab) {
		case "new":
		case "my":
			return q.orderBy("assigneeId", "asc").orderBy("state", "asc")
		case "close-loop":
			return q
				.orderBy("state", "asc")
				.orderBy("createdAt", "desc")
				.orderBy("id", "desc")
		case "replied":
		case "needs-response":
			return q
				.orderBy("replied", "asc")
				.orderBy("state", "asc")
				.orderBy("lastMessageAt", "desc")
				.orderBy("id", "desc")
		// A custom-view badge counts its Open sub-tab or its own status filter, both on state.
		case "open":
		case "agent-pending":
		case "snoozed":
		case "done":
		case "custom-view":
		case undefined:
			return q
				.orderBy("state", "asc")
				.orderBy("lastMessageAt", "desc")
				.orderBy("id", "desc")
		default:
			return q
	}
}

function applyTabFilter(
	q: Query<"feedbacks", Schema>,
	workspaceId: string,
	threadState?: InboxState,
	userID?: string,
	nowMs?: number,
	stateScope?: z.infer<typeof stateScopeSchema>,
) {
	// When a sub-tab scope is active, the base tab only keeps its identity
	// filter (assignee / SLA / etc.); open / snoozed / done comes from scope.
	const scoped = stateScope != null

	switch (threadState) {
		case "snoozed":
			return q.where("state", "=", "SNOOZED")
		case "done":
			return q.where("state", "=", "PROCESSED")
		case "open":
			return q.where(({ and, cmp }) => and(cmp("state", "IN", OPEN_STATES)))
		case "close-loop":
			return q.where("state", "=", "COMPLETED")
		// A close-loop thread assigned to me is still mine to finish, so it lists
		// under My inbox as well as Close loop. All keeps leaving them out.
		case "my":
			return q.where(({ and, cmp, not }) =>
				and(
					cmp("assigneeId", userID ?? ""),
					not(cmp("state", "SNOOZED")),
					not(cmp("state", "PROCESSED")),
				),
			)
		case "my-snoozed":
			return q.where(({ and, cmp }) =>
				and(cmp("assigneeId", userID ?? ""), cmp("state", "SNOOZED")),
			)
		case "my-done":
			return q.where(({ and, cmp }) =>
				and(cmp("assigneeId", userID ?? ""), cmp("state", "PROCESSED")),
			)
		case "my-close-loop":
			return q.where(({ and, cmp }) =>
				and(cmp("assigneeId", userID ?? ""), cmp("state", "COMPLETED")),
			)
		case "replied":
			return q.where(({ and, cmp }) =>
				and(
					cmp("state", "IN", OPEN_STATES),
					cmp("lastOutboundMessageAt", "IS NOT", null),
					cmp("replied", true),
				),
			)
		case "new":
			if (scoped && stateScope !== "open") {
				return q.where("assigneeId", "IS", null)
			}
			return q.where(({ and, cmp, or }) =>
				and(
					or(cmp("state", "NEW"), cmp("state", "UNSNOOZED")),
					cmp("assigneeId", "IS", null),
				),
			)
		case "sla-breached":
			if (scoped && stateScope !== "open") {
				return q.where(({ and, cmp }) =>
					and(
						cmp("replied", false),
						cmp("slaDeadlineAt", ">", 0),
						cmp("slaDeadlineAt", "<", nowMs ?? Date.now()),
					),
				)
			}
			return q.where(({ and, cmp }) =>
				and(
					cmp("replied", false),
					cmp("state", "IN", OPEN_STATES),
					cmp("slaDeadlineAt", ">", 0),
					cmp("slaDeadlineAt", "<", nowMs ?? Date.now()),
				),
			)
		// The three Agent tabs read the session outcome, which is also what the
		// monthly invoice counts, so Resolved lists exactly the billed ones.
		case "agent-resolved":
			return q.whereExists(
				"aiChatSessions",
				(s) => agentSessions(s, workspaceId).where("isResolved", true),
				{ flip: true },
			)
		// Every handoff nobody has reviewed, even once the thread is done or closed.
		case "agent-escalated":
			return escalatedThreads(q, workspaceId).where(
				"agentEscalationReviewedAt",
				"IS",
				null,
			)
		case "agent-reviewed":
			return escalatedThreads(q, workspaceId).where(
				"agentEscalationReviewedAt",
				"IS NOT",
				null,
			)
		// The agent marks a thread done once its follow-up sends, so state cannot
		// say which loops it closed; the send activity can, whatever the state.
		case "agent-loops-closed":
			return q.whereExists(
				"threadActivities",
				(a) =>
					a
						.where("workspaceId", workspaceId)
						.where("isDeleted", false)
						.where("eventType", "close_loop_sent"),
				{ flip: true },
			)
		case "agent-pending":
			// A thread someone already processed, snoozed, or closed the loop on is
			// handled, whatever its session still says.
			return q
				.where(({ and, cmp }) => and(cmp("state", "IN", OPEN_STATES)))
				.whereExists("aiChatSessions", (s) =>
					agentSessions(s, workspaceId)
						.where(({ or, cmp }) =>
							or(cmp("isResolved", false), cmp("isResolved", "IS", null)),
						)
						.where("escalatedAt", "IS", null),
				)
		case "needs-response":
			if (scoped && stateScope !== "open") {
				return q.where("replied", false)
			}
			return q.where(({ and, cmp }) =>
				and(cmp("replied", false), cmp("state", "IN", OPEN_STATES)),
			)

		default:
			/// INBOX
			return q.where(({ and, cmp, not }) =>
				and(
					not(cmp("assigneeId", userID ?? "")),
					cmp("state", "IN", OPEN_STATES),
					cmp("replied", false),
				),
			)
	}
}

// Tabs that keep their identity filter and narrow open / snoozed / done via
// stateScope (as opposed to encoding it in the tab name like my-snoozed).
function shouldApplyStateScope(
	tab: InboxState | undefined,
	scope?: z.infer<typeof stateScopeSchema>,
): boolean {
	if (scope == null) return false
	return (
		tab === "custom-view" ||
		tab === "new" ||
		tab === "sla-breached" ||
		tab === "needs-response"
	)
}

// Scopes a query to open / snoozed / done / close-loop by thread state,
// independent of the tab. Used by custom views and by built-in tabs that expose
// the same sub-tabs.
function applyStateScope(
	q: Query<"feedbacks", Schema>,
	scope?: z.infer<typeof stateScopeSchema>,
) {
	switch (scope) {
		case "snoozed":
			return q.where("state", "=", "SNOOZED")
		case "done":
			return q.where("state", "=", "PROCESSED")
		case "close-loop":
			return q.where("state", "=", "COMPLETED")
		case "open":
			return q.where(({ and, cmp }) => and(cmp("state", "IN", OPEN_STATES)))
		default:
			return q
	}
}

function applySortFilters(
	q: Query<"feedbacks", Schema>,
	column: SortByColumn,
	order: SortByColumnOrder,
) {
	return q.orderBy(column, order).orderBy("id", order)
}

export const feedbackMessagePreloadSize = 3

// A thread row shows one preview line, so each message relation carries only the
// newest row. Zero has no column projection, so a relation the list does not
// read still syncs every column, and an email htmlContent runs to megabytes.
function applyThreadListRelations(
	q: Query<"feedbacks", Schema>,
	workspaceId: string,
) {
	return q
		.related("company", (c) =>
			c
				.where("isDeleted", false)
				.where("workspaceId", workspaceId)
				.related("companyMeta"),
		)
		.related("contact", (c) =>
			c.where("isDeleted", false).where("workspaceId", workspaceId),
		)
		.related("emails", (e) =>
			e
				.where("isDeleted", false)
				.where("workspaceId", workspaceId)
				// The preview skips auto-replies, so filtering here keeps one row enough.
				.where(({ or, cmp }) =>
					or(cmp("source", "IS", null), cmp("source", "!=", "AUTO_REPLY")),
				)
				.orderBy("createdAt", "desc")
				.orderBy("id", "desc")
				.limit(1),
		)
		.related("slackMessages", (s) =>
			s
				.where("isDeleted", false)
				.where("workspaceId", workspaceId)
				.orderBy("createdAt", "desc")
				.orderBy("id", "desc")
				.limit(1),
		)
		.related("chatMessages", (s) =>
			s
				.where("isDeleted", false)
				.where("workspaceId", workspaceId)
				.orderBy("createdAt", "desc")
				.orderBy("id", "desc")
				.limit(1),
		)
		.related("threadMessages", (t) =>
			t
				.where("isDeleted", false)
				.where("workspaceId", workspaceId)
				.orderBy("createdAt", "desc")
				.orderBy("id", "desc")
				.limit(1),
		)
		.related("assignee")
		.related("issues")
		.related("projects")
		.related("tags", (t) => t.where("isDeleted", false))
		.related("liveChatSession", (l) =>
			l
				.related("messages", (q) =>
					q.where("isDeleted", false).orderBy("createdAt", "desc").limit(1),
				)
				.where("workspaceId", workspaceId)
				.orderBy("createdAt", "desc")
				.orderBy("id", "desc")
				.limit(1),
		)
}

/**
 * `not`/`or` are table independent, so one instantiation types them all.
 */
type NegatableEb = Pick<ExpressionBuilder<"feedbacks", Schema>, "not" | "or">

/**
 * Matches a column against a list of values, where a null entry in the list
 * means "has no value". Negated, it keeps rows without a value unless "has no
 * value" was itself selected.
 */
function columnListCondition(
	eb: NegatableEb,
	{
		inList,
		isEmpty,
		hasEmpty,
		isNegated,
	}: {
		inList: Condition | undefined
		isEmpty: Condition
		hasEmpty: boolean
		isNegated: boolean
	},
): Condition {
	const positive =
		inList && hasEmpty ? eb.or(inList, isEmpty) : (inList ?? isEmpty)
	if (!isNegated) return positive
	return hasEmpty ? eb.not(positive) : eb.or(eb.not(positive), isEmpty)
}

/**
 * `company` hangs off companyId, so a negated company condition reads as "no
 * company, or a company that fails it" - the client cannot run not(exists()).
 */
function companyRelationCondition(
	eb: ExpressionBuilder<"feedbacks", Schema>,
	isNegated: boolean,
	inner: (ceb: ExpressionBuilder<"companies", Schema>) => Condition,
): Condition {
	const matches = eb.exists("company", (c) => c.where(inner))
	return isNegated ? eb.or(eb.cmp("companyId", "IS", null), matches) : matches
}

interface ThreadJunctionFilters {
	negations?: ThreadFilterNegations
	projectIds?: Array<string | null>
	tagIds?: Array<string | null>
	issueIds?: Array<string | null>
}

/**
 * Tags, projects and issues hang off junction tables, so "is not" would need a
 * not(exists()) the Zero client cannot run. The pickers no longer offer it, but
 * saved views and persisted state still can.
 *
 * "No issue linked" is the exception: it reads `hasLinkedIssue`, a column a
 * database trigger keeps, so its null survives. Tags and projects have no such
 * column yet, so theirs still falls away.
 */
export function clientSafeThreadFilters<T extends ThreadJunctionFilters>(
	filters: T,
): T {
	const next = { ...filters }
	const negations: ThreadFilterNegations = { ...filters.negations }
	for (const key of ["projectIds", "tagIds", "issueIds"] as const) {
		const values = next[key]
		if (!values || values.length === 0) continue
		// Keeping the values and dropping the operator would invert the filter.
		if (negations[key]) {
			delete negations[key]
			next[key] = []
		} else if (key !== "issueIds") {
			next[key] = values.filter((id) => id !== null)
		}
	}
	next.negations = negations
	return next
}

interface RoadmapRelationFilters {
	negations?: RoadmapFilterNegations
	teamIds?: string[]
	priorities?: string[]
	companyIds?: Array<string | null>
}

/**
 * Projects reach teams and priorities through their issues, and companies
 * through customerNeeds, so negating those needs a not(exists()) too.
 */
export function clientSafeRoadmapFilters<T extends RoadmapRelationFilters>(
	filters: T,
): T {
	const next = { ...filters }
	const negations: RoadmapFilterNegations = { ...filters.negations }
	for (const key of ["teamIds", "priorities", "companyIds"] as const) {
		if (!negations[key]) continue
		delete negations[key]
		next[key] = []
	}
	next.negations = negations
	return next
}

// Reads the same `replied` flag (and index) as the built-in Needs response tab.
export function repliedValuesFor(
	replyStates: readonly ReplyStateFilter[],
	isNegated: boolean,
): boolean[] {
	return [false, true].filter(
		(replied) =>
			replyStates.includes(replied ? "REPLIED" : "NEEDS_RESPONSE") !==
			isNegated,
	)
}

// The status filter shows one "Open" option backed by NEW, so a thread woken
// from snooze has to ride along or it falls out of every saved view.
export function expandOpenStatuses(
	statuses: readonly FeedbackStateFilter[],
): FeedbackStateFilter[] {
	if (!statuses.includes("NEW") || statuses.includes("UNSNOOZED")) {
		return [...statuses]
	}
	return [...statuses, "UNSNOOZED"]
}

export function applyThreadFilters(
	q: Query<"feedbacks", Schema>,
	rawFilters: ThreadFilters,
) {
	const {
		negations,
		origins,
		emailAddressIds,
		portalInstanceIds,
		creatorIds,
		painLevels,
		assigneeIds,
		projectIds,
		tagIds,
		issueIds,
		companyIds,
		personIds,
		statuses,
		replyStates,
		companyTiers,
		companyStatuses,
		companyOwnerIds,
		issueStatuses,
		slackWorkspaceIds,
		slackChannelIds,
		companyRevenue,
		companySize,
	} = clientSafeThreadFilters(rawFilters)
	const not = negations ?? {}
	if (slackChannelIds && slackChannelIds.length > 0) {
		q = q.where((eb) =>
			columnListCondition(eb, {
				inList: eb.cmp("slackChannelId", "IN", slackChannelIds),
				isEmpty: eb.cmp("slackChannelId", "IS", null),
				hasEmpty: false,
				isNegated: not.slackChannelIds ?? false,
			}),
		)
	}
	// The client rejects NOT EXISTS, so "not these workspaces" reads as a thread
	// from outside Slack, or one whose channel belongs to another workspace.
	if (slackWorkspaceIds && slackWorkspaceIds.length > 0) {
		if (not.slackWorkspaceIds) {
			q = q.where((eb) =>
				eb.or(
					eb.cmp("slackChannelId", "IS", null),
					eb.exists("slackChannel", (c) =>
						c.where("slackConnectSettingsId", "NOT IN", slackWorkspaceIds),
					),
				),
			)
		} else {
			q = q.whereExists(
				"slackChannel",
				(c) => c.where("slackConnectSettingsId", "IN", slackWorkspaceIds),
				{ flip: true },
			)
		}
	}
	if (origins && origins.length > 0) {
		const nonNullOrigins = origins.filter(
			(origin): origin is FeedbackOrigin => origin !== null,
		)
		if (nonNullOrigins.length > 0) {
			q = q.where(
				"origin",
				not.origins ? "NOT IN" : "IN",
				nonNullOrigins as readonly FeedbackOrigin[],
			)
		}
	}
	if (emailAddressIds && emailAddressIds.length > 0) {
		const ids = emailAddressIds.filter((id) => id !== null)

		q = q.where((eb) =>
			columnListCondition(eb, {
				inList:
					ids.length > 0 ? eb.cmp("emailAddressId", "IN", ids) : undefined,
				isEmpty: eb.cmp("emailAddressId", "IS", null),
				hasEmpty: emailAddressIds.some((id) => id === null),
				isNegated: not.emailAddressIds ?? false,
			}),
		)
	}
	// A null id is the main portal, which stores no PortalInstance row.
	if (portalInstanceIds && portalInstanceIds.length > 0) {
		const ids = portalInstanceIds.filter((id) => id !== null)

		q = q.where((eb) =>
			columnListCondition(eb, {
				inList:
					ids.length > 0 ? eb.cmp("portalInstanceId", "IN", ids) : undefined,
				isEmpty: eb.cmp("portalInstanceId", "IS", null),
				hasEmpty: portalInstanceIds.some((id) => id === null),
				isNegated: not.portalInstanceIds ?? false,
			}),
		)
	}
	if (creatorIds && creatorIds.length > 0) {
		q = q.where((eb) =>
			columnListCondition(eb, {
				inList: eb.cmp("reporterId", "IN", creatorIds),
				isEmpty: eb.cmp("reporterId", "IS", null),
				hasEmpty: false,
				isNegated: not.creatorIds ?? false,
			}),
		)
	}
	if (painLevels && painLevels.length > 0) {
		q = q.where(
			"painLevel",
			not.painLevels ? "NOT IN" : "IN",
			painLevels as readonly PainLevel[],
		)
	}
	if (assigneeIds && assigneeIds.length > 0) {
		const ids = assigneeIds.filter((id) => id !== null)

		q = q.where((eb) =>
			columnListCondition(eb, {
				inList: ids.length > 0 ? eb.cmp("assigneeId", "IN", ids) : undefined,
				isEmpty: eb.cmp("assigneeId", "IS", null),
				hasEmpty: assigneeIds.some((id) => id === null),
				isNegated: not.assigneeIds ?? false,
			}),
		)
	}
	if (projectIds && projectIds.length > 0) {
		const ids = projectIds.filter((id): id is string => id !== null)

		// Project, tag and issue joins start from the picked rows. Unflipped, a rare
		// pick walks every thread in the workspace (prod runs Zero without a planner).
		if (ids.length > 0) {
			q = q.whereExists("projects", (p) => p.where("id", "IN", ids), {
				flip: true,
			})
		}
	}
	if (tagIds && tagIds.length > 0) {
		const ids = tagIds.filter((id): id is string => id !== null)

		if (ids.length > 0) {
			q = q.whereExists("tags", (t) => t.where("id", "IN", ids), {
				flip: true,
			})
		}
	}
	if (issueIds && issueIds.length > 0) {
		const ids = issueIds.filter((id): id is string => id !== null)
		const wantsNone = issueIds.some((id) => id === null)

		// "No issue linked" reads a trigger-kept flag (the client rejects NOT EXISTS).
		// Not flipped next to it: the OR scans every thread for the flag anyway.
		if (ids.length > 0 && wantsNone) {
			q = q.where((eb) =>
				eb.or(
					eb.cmp("hasLinkedIssue", false),
					eb.exists("issues", (i) => i.where("id", "IN", ids)),
				),
			)
		} else if (wantsNone) {
			q = q.where("hasLinkedIssue", false)
		} else if (ids.length > 0) {
			q = q.whereExists("issues", (i) => i.where("id", "IN", ids), {
				flip: true,
			})
		}
	}
	// Matches the state name across every Linear team. Flipped because issues in
	// a state are far fewer than threads.
	if (issueStatuses && issueStatuses.length > 0) {
		q = q.whereExists(
			"issues",
			(i) =>
				i.where("isDeleted", false).where("statusName", "IN", issueStatuses),
			{ flip: true },
		)
	}
	if (companyIds && companyIds.length > 0) {
		const ids = companyIds.filter((id) => id !== null)

		q = q.where((eb) =>
			columnListCondition(eb, {
				inList: ids.length > 0 ? eb.cmp("companyId", "IN", ids) : undefined,
				isEmpty: eb.cmp("companyId", "IS", null),
				hasEmpty: companyIds.some((id) => id === null),
				isNegated: not.companyIds ?? false,
			}),
		)
	}
	if (personIds && personIds.length > 0) {
		const ids = personIds.filter((id) => id !== null)

		// Match on contactId rather than the contact relation: "no person linked"
		// through the relation needs a not(exists()) the client cannot run.
		q = q.where((eb) =>
			columnListCondition(eb, {
				inList: ids.length > 0 ? eb.cmp("contactId", "IN", ids) : undefined,
				isEmpty: eb.cmp("contactId", "IS", null),
				hasEmpty: personIds.some((id) => id === null),
				isNegated: not.personIds ?? false,
			}),
		)
	}
	if (statuses && statuses.length > 0) {
		q = q.where(
			"state",
			not.statuses ? "NOT IN" : "IN",
			expandOpenStatuses(statuses) as readonly FeedbackStateFilter[],
		)
	}
	if (replyStates && replyStates.length > 0) {
		const replied = repliedValuesFor(replyStates, not.replyStates ?? false)
		if (replied.length === 1) {
			q = q.where("replied", replied[0])
		} else if (replied.length === 0) {
			q = q.where(({ and, cmp }) =>
				and(cmp("replied", true), cmp("replied", false)),
			)
		}
	}
	if (companyTiers && companyTiers.length > 0) {
		const isNegated = not.companyTiers ?? false
		q = q.where((eb) =>
			companyRelationCondition(eb, isNegated, (ceb) =>
				columnListCondition(ceb, {
					inList: ceb.cmp("tierName", "IN", companyTiers),
					isEmpty: ceb.cmp("tierName", "IS", null),
					hasEmpty: false,
					isNegated,
				}),
			),
		)
	}
	if (companyStatuses && companyStatuses.length > 0) {
		const isNegated = not.companyStatuses ?? false
		q = q.where((eb) =>
			companyRelationCondition(eb, isNegated, (ceb) =>
				columnListCondition(ceb, {
					inList: ceb.cmp("statusName", "IN", companyStatuses),
					isEmpty: ceb.cmp("statusName", "IS", null),
					hasEmpty: false,
					isNegated,
				}),
			),
		)
	}
	if (companyOwnerIds && companyOwnerIds.length > 0) {
		const ids = companyOwnerIds.filter((id) => id !== null)
		const hasEmpty = companyOwnerIds.some((id) => id === null)
		const isNegated = not.companyOwnerIds ?? false

		q = q.where((eb) =>
			companyRelationCondition(eb, isNegated, (ceb) =>
				columnListCondition(ceb, {
					inList:
						ids.length > 0 ? ceb.cmp("linearOwnerId", "IN", ids) : undefined,
					isEmpty: ceb.cmp("linearOwnerId", "IS", null),
					hasEmpty,
					isNegated,
				}),
			),
		)
	}
	if (companyRevenue) {
		const { operator, value } = companyRevenue
		q = q.whereExists("company", (c) => c.where("revenue", operator, value))
	}
	if (companySize) {
		const { operator, value } = companySize
		q = q.whereExists("company", (c) => c.where("size", operator, value))
	}
	return q
}

function applyProjectTabFilter(q: Query<"projects", Schema>, tab: RoadmapTab) {
	switch (tab) {
		case "hidden":
			return q.where("isVisible", false)
		case "active":
			return q.where("isVisible", true)
		case "pending":
			return q
				.where("isVisible", "IS", null)
				.where("state", "!=", "canceled")
				.where("state", "!=", "completed")
				.where("state", "!=", "closed")
		case "all":
			return q
	}
}

function applyProjectRelations(
	q: Query<"projects", Schema>,
	withLabels = false,
) {
	return q
		.where("isDeleted", false)
		.related("customerNeeds", (cn) => cn.related("feedback"))
		.related("issues", (i) => {
			const issues = i
				.where("isDeleted", false)
				.where("status", "!=", "canceled")
				.related("customerNeeds", (cn) => cn.related("feedback"))
			return withLabels ? issues.related("labels") : issues
		})
		.related("linearRoadmaps", (l) => l.where("isDeleted", false))
		.related("translations")
}

function applyIssueTabFilter(q: Query<"issues", Schema>, tab: RoadmapTab) {
	switch (tab) {
		case "hidden":
			return q.where("isVisible", false)
		case "active":
			return q.where("isVisible", true)
		case "pending":
			return q
				.where("isVisible", "IS", null)
				.where("status", "!=", "triage")
				.where("status", "!=", "duplicate")
				.where("status", "!=", "canceled")
				.where("status", "!=", "completed")
		case "all":
			return q
	}
}

function applyRoadmapSort<Q extends { orderBy: (...args: never[]) => Q }>(
	q: Q,
	sortOrder: RoadmapSortOrder,
	scoreDirection: "asc" | "desc" = "desc",
): Q {
	const orderBy = (query: Q, column: string, direction: "asc" | "desc") =>
		(query.orderBy as unknown as (c: string, d: string) => Q)(column, direction)
	let ordered: Q
	switch (sortOrder) {
		case "LINEAR":
			ordered = orderBy(q, "sortOrder", "asc")
			break
		case "LINEAR_REVERSE":
			ordered = orderBy(q, "sortOrder", "desc")
			break
		case "SCORE_ASC":
			ordered = orderBy(q, "totalScore", "asc")
			break
		case "CUSTOM":
			// Descending puts never-placed (null) rows last; see roadmap-custom-order.
			ordered = orderBy(
				orderBy(q, "portalSortOrder", "desc"),
				"totalScore",
				"desc",
			)
			break
		default:
			ordered = orderBy(q, "totalScore", scoreDirection)
	}
	return (ordered.orderBy as unknown as (c: string, d: string) => Q)(
		"id",
		"asc",
	)
}

function applyIssueRelations(q: Query<"issues", Schema>, withLabels = false) {
	// The requests list reads customerNeeds.length and nothing else. The nested
	// feedback row and the translations were pure payload, and on a workspace
	// with thousands of issues they were most of the sync that froze the page.
	const issues = q.where("isDeleted", false).related("customerNeeds")
	return withLabels ? issues.related("labels") : issues
}

export function applyRoadmapProjectFilters(
	q: Query<"projects", Schema>,
	rawFilters: RoadmapFilters,
) {
	const { negations, statuses, projectIds, priorities, companyIds, sources } =
		clientSafeRoadmapFilters(rawFilters)
	if (roadmapSource(sources) === "customers") {
		q = q.whereExists("customerNeeds")
	}
	const not = negations ?? {}
	const labelIds = rawFilters.labelIds ?? []
	if (labelIds.length > 0 && !rawFilters.negations?.labelIds) {
		q = q.whereExists("issues", (i) =>
			i.whereExists("labels", (l) => l.where("labelId", "IN", labelIds)),
		)
	}
	if (statuses && statuses.length > 0) {
		// `state` is nullable, and NOT IN drops null rows, so a project with no
		// state would disappear from "status is not X".
		q = q.where((eb) =>
			columnListCondition(eb, {
				inList: eb.cmp("state", "IN", statuses),
				isEmpty: eb.cmp("state", "IS", null),
				hasEmpty: false,
				isNegated: not.statuses ?? false,
			}),
		)
	}
	if (projectIds && projectIds.length > 0) {
		const ids = projectIds.filter((id): id is string => id !== null)
		if (ids.length > 0) {
			q = q.where("id", not.projectIds ? "NOT IN" : "IN", ids)
		}
	}
	// A project's own teams live in the `linearTeamIds` array, which ZQL cannot
	// filter, so the team gate runs client-side. Reading it off the issues here
	// dropped every project that has none.
	// Priority and company only support "is": see clientSafeRoadmapFilters.
	if (priorities && priorities.length > 0) {
		q = q.whereExists("issues", (i) => i.where("priority", "IN", priorities))
	}
	if (companyIds && companyIds.length > 0) {
		const ids = companyIds.filter((id): id is string => id !== null)
		if (ids.length > 0) {
			q = q.whereExists("customerNeeds", (cn) =>
				cn.whereExists("feedback", (f) => f.where("companyId", "IN", ids)),
			)
		}
	}
	return q
}

function applyCompaniesFilters(
	q: Query<"companies", Schema>,
	{
		negations,
		tiers,
		statuses,
		ownerIds,
		revenue,
		size,
		sources,
	}: CompaniesFilters,
) {
	const not = negations ?? {}
	if (tiers && tiers.length > 0) {
		q = q.where((eb) =>
			columnListCondition(eb, {
				inList: eb.cmp("tierName", "IN", tiers),
				isEmpty: eb.cmp("tierName", "IS", null),
				hasEmpty: false,
				isNegated: not.tiers ?? false,
			}),
		)
	}
	if (sources && sources.length > 0) {
		const externalIdColumns = [
			"attioId",
			"hubspotId",
			"intercomId",
			"productboardId",
		] as const
		q = q.where(({ or, and, cmp, not: negate }) => {
			const clauses = sources.map((source) => {
				if (source === "productlane") {
					return and(...externalIdColumns.map((col) => cmp(col, "IS", null)))
				}
				const col = `${source}Id` as (typeof externalIdColumns)[number]
				if (!externalIdColumns.includes(col)) {
					// zendesk has no column on companies; never matches
					return cmp("id", "IS", null)
				}
				return cmp(col, "IS NOT", null)
			})
			const positive = clauses.length === 1 ? clauses[0]! : or(...clauses)
			return not.sources ? negate(positive) : positive
		})
	}
	if (statuses && statuses.length > 0) {
		q = q.where((eb) =>
			columnListCondition(eb, {
				inList: eb.cmp("statusName", "IN", statuses),
				isEmpty: eb.cmp("statusName", "IS", null),
				hasEmpty: false,
				isNegated: not.statuses ?? false,
			}),
		)
	}
	if (ownerIds && ownerIds.length > 0) {
		const ids = ownerIds.filter((id): id is string => id !== null)
		q = q.where((eb) =>
			columnListCondition(eb, {
				inList: ids.length > 0 ? eb.cmp("linearOwnerId", "IN", ids) : undefined,
				isEmpty: eb.cmp("linearOwnerId", "IS", null),
				hasEmpty: ownerIds.some((id) => id === null),
				isNegated: not.ownerIds ?? false,
			}),
		)
	}
	if (revenue) {
		q = q.where("revenue", revenue.operator, revenue.value)
	}
	if (size) {
		q = q.where("size", size.operator, size.value)
	}
	return q
}

function applyContactsFilters(
	q: Query<"contacts", Schema>,
	{ negations, companyIds, subscribedStates, sources }: ContactsFilters,
) {
	const not = negations ?? {}
	if (sources && sources.length > 0) {
		const externalIdColumns = [
			"attioId",
			"hubspotId",
			"intercomId",
			"productboardId",
			"zendeskId",
		] as const
		q = q.where(({ or, and, cmp, not: negate }) => {
			const clauses = sources.map((source) => {
				if (source === "productlane") {
					return and(...externalIdColumns.map((col) => cmp(col, "IS", null)))
				}
				const col = `${source}Id` as (typeof externalIdColumns)[number]
				return cmp(col, "IS NOT", null)
			})
			const positive = clauses.length === 1 ? clauses[0]! : or(...clauses)
			return not.sources ? negate(positive) : positive
		})
	}
	if (companyIds && companyIds.length > 0) {
		const ids = companyIds.filter((id): id is string => id !== null)
		const isNegated = not.companyIds ?? false
		q = q.where((eb) =>
			columnListCondition(eb, {
				// "Is" matches the primary or any additional membership. "Is not"
				// stays on the primary: Zero cannot run not(exists()) client-side.
				inList:
					ids.length === 0
						? undefined
						: isNegated
							? eb.cmp("companyId", "IN", ids)
							: eb.or(
									eb.cmp("companyId", "IN", ids),
									eb.exists("companyLinks", (l) =>
										l.where("companyId", "IN", ids),
									),
								),
				isEmpty: eb.cmp("companyId", "IS", null),
				hasEmpty: companyIds.some((id) => id === null),
				isNegated,
			}),
		)
	}
	if (subscribedStates && subscribedStates.length > 0) {
		q = q.where(
			"isSubscribed",
			not.subscribedStates ? "NOT IN" : "IN",
			subscribedStates,
		)
	}
	return q
}

function applyRoadmapIssueFilters(
	q: Query<"issues", Schema>,
	rawFilters: RoadmapFilters,
) {
	const {
		negations,
		statuses,
		teamIds,
		projectIds,
		priorities,
		companyIds,
		sources,
	} = clientSafeRoadmapFilters(rawFilters)
	if (roadmapSource(sources) === "customers") {
		q = q.whereExists("customerNeeds")
	}
	const not = negations ?? {}
	const labelIds = rawFilters.labelIds ?? []
	if (labelIds.length > 0 && !rawFilters.negations?.labelIds) {
		q = q.whereExists("labels", (l) => l.where("labelId", "IN", labelIds))
	}
	if (statuses && statuses.length > 0) {
		q = q.where("status", not.statuses ? "NOT IN" : "IN", statuses)
	}
	if (teamIds && teamIds.length > 0) {
		q = q.where("team", "IN", teamIds)
	}
	if (projectIds && projectIds.length > 0) {
		const ids = projectIds.filter((id): id is string => id !== null)
		q = q.where((eb) =>
			columnListCondition(eb, {
				inList: ids.length > 0 ? eb.cmp("projectId", "IN", ids) : undefined,
				isEmpty: eb.cmp("projectId", "IS", null),
				hasEmpty: projectIds.some((id) => id === null),
				isNegated: not.projectIds ?? false,
			}),
		)
	}
	if (priorities && priorities.length > 0) {
		q = q.where("priority", "IN", priorities)
	}
	if (companyIds && companyIds.length > 0) {
		const ids = companyIds.filter((id): id is string => id !== null)
		if (ids.length > 0) {
			q = q.whereExists("customerNeeds", (cn) =>
				cn.whereExists("feedback", (f) => f.where("companyId", "IN", ids)),
			)
		}
	}
	return q
}
